import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { deviceTypeLabel } from '../../../utils/terminology';
import { PersonDetailData } from '../index';
import { ShieldCheck, Plus, X, MapPin, RefreshCw, Check, AlertCircle, Clock, ShieldOff, Trash2, Monitor, Wifi, WifiOff, DoorOpen, XCircle, CheckCircle, Copy, ChevronDown, ChevronUp, HelpCircle, Home, Info } from 'lucide-react';
import md5 from 'md5';
import { useKeycloak } from '../../../contexts/KeycloakContext';
import { RfidDevicesSection } from './RfidDevicesSection';
import VisitorAuthorizerPersonToggle from './VisitorAuthorizerPersonToggle';
import { api } from '../../../services/api';
import { toast } from 'react-hot-toast';
import { notify } from '../../../utils/notify';
import { DeviceAccessTable } from './DeviceAccessTable';
import { accessNotInEffectReason } from './accessInEffect';
import { cn } from '../../../utils/cn';
import { DeviceDetailsModal, IoTDevice, Zone } from '../../../pages/ZonesDevices';
import { PersonAuditLogTable } from '../../PersonAuditLogTable';
import { useDeviceCommunication } from '../../../hooks/useDeviceCommunication';
import { DeviceOperations, DeviceInfo, CommunicationMode, apiDeviceToDeviceInfo, uuidToDeterministicControlIDId } from '../../../services/deviceCommunication';
import { DeviceIntegration } from '../../../integrations/devices';
import { PersonData } from '../../../services/adapters';
import { ConnectionModeIndicator } from '../../ConnectionModeIndicator';
import { useConfirm } from '../../ui/ConfirmDialog';
import { useSyncProgress } from '../../../contexts/SyncProgressContext';
import { SyncProgressItem } from '../../SyncProgressModal';
import UnitSelector, { UnitOption } from '../../UnitSelector';
import { UnitAssignmentManager } from '../../UnitAssignmentManager';
import { customerService } from '../../../services/customerService';
import { listRefreshEvents } from '../../../services/listRefreshEvents';
import { humanizeDeviceSyncError, LOCAL_UNREACHABLE } from '../../../utils/deviceSyncError';
import { buildPersonSyncItem, buildPersonSyncTitle } from '../../../utils/syncProgressPresenter';
import { buildPersonSyncPayload } from '../../../domain/personSyncData';
import {
  isUnitBasedRole,
  isDepartmentBasedRole,
  isFallbackDepartment,
  roleTypeLabel,
  availableRoleTypesFor,
  FALLBACK_DEPARTMENT_NAME,
} from './roleTaxonomy';
import { syncDeviceAndReport } from '../../../utils/syncDeviceAndReport';
import { reportDeviceSyncOutcome } from '../../../utils/deviceSyncOutcome';

/**
 * Roles whose access is RESOLVED through a unit.
 *
 * Only residents. unit_profile_assignments is joined on
 * person_roles.role_metadata->>'unit_id' AND gated to role_type IN ('resident','morador')
 * (access_profile_service.go), so a Morador with no unit inherits no profile and is granted
 * nothing — that, and only that, is what justifies demanding a unit before the role is created.
 *
 * Visitante and prestador were briefly included here on the theory that they "visit a unit".
 * That was wrong twice over: an empresa customer has no units or blocks at all, so the picker
 * asked a company to choose a bloco and a "Proprietário/Inquilino" relação; and because the
 * unit was required, a Visitante could not be created for a company at all. Their unit, where
 * it exists, belongs to the visit — not to the role that gates access.
 *
 * Also excluded: porteiro and sindico, which are condomínio-wide.
 */
interface DepartmentOption {
  id: string;
  name: string;
}

/** Mirrors UnitAssignmentManager's options, so both entry points speak the same vocabulary. */
const ROLE_RELATION_OPTIONS = [
  { value: 'proprietario', label: 'Proprietário' },
  { value: 'inquilino', label: 'Inquilino' },
  { value: 'dependente', label: 'Dependente' },
  { value: 'familiar', label: 'Familiar' },
];

interface RoleUnitAssignment {
  unit_id: string;
  unit_number?: string;
  block_name?: string;
  street_name?: string;
  street_number?: string;
  relation_type: string;
  parentesco?: string;
}

/**
 * The units on a role, from either metadata shape.
 *
 * PatchPersonRoleMetadata writes `unit_assignments` AND mirrors the first one into the flat
 * `unit_id` / `unit_number` / `relation_type` keys. Roles created before unit_assignments
 * existed only have the flat keys, so both have to be read or older residents would look
 * unit-less and get flagged as granting nothing.
 */
function readRoleUnits(roleMetadata: any): RoleUnitAssignment[] {
  if (!roleMetadata) return [];

  const assignments = roleMetadata.unit_assignments;
  if (Array.isArray(assignments) && assignments.length > 0) {
    return assignments.filter((a: any) => a?.unit_id);
  }

  if (roleMetadata.unit_id) {
    return [{
      unit_id: roleMetadata.unit_id,
      unit_number: roleMetadata.unit_number,
      block_name: roleMetadata.block_name,
      relation_type: roleMetadata.relation_type || 'proprietario',
      parentesco: roleMetadata.parentesco,
    }];
  }

  return [];
}

/** "101", "Bloco A 101", or the raw id when the number was never denormalized into metadata. */
function describeRoleUnit(assignment: RoleUnitAssignment): string {
  const number = assignment.unit_number
    || [assignment.street_name, assignment.street_number].filter(Boolean).join(', ')
    || assignment.unit_id;
  return assignment.block_name ? `${assignment.block_name} ${number}` : String(number);
}

// Translation function to convert English zone types to Portuguese
const translateZoneType = (type: string): string => {
  const translations: Record<string, string> = {
    'entrance': 'Entrada',
    'common_area': 'Área Comum',
    'private': 'Privada',
    'restricted': 'Restrita',
    'parking': 'Estacionamento',
    'elevator': 'Elevador',
    'gate': 'Portão',
  };
  return translations[type] || type;
};

interface SyncStatus {
  isSyncing: boolean;
  lastSyncAt: Date | null;
  syncResult: 'success' | 'error' | 'partial' | null;
  syncMessage: string;
  successfulDevices?: { device_id: string; device_name: string }[];
  failedDevices?: { device_id: string; device_name: string; error: string }[];
  successLabel?: string;
  failedLabel?: string;
}

interface ConfirmModalConfig {
  title: string;
  message: string;
  confirmText: string;
  confirmButtonClass: string;
  icon: 'warning' | 'entry' | 'exit' | 'revoke';
  showDisclaimer?: boolean;
  disclaimerType?: 'entry' | 'revoke' | 'general';
  onConfirm: () => Promise<void> | void;
}

interface PermissionsTabProps {
  person: PersonDetailData;
  onRefresh: () => void;
  readOnly?: boolean;
  darkMode?: boolean;
  syncStatus: SyncStatus;
  setSyncStatus: React.Dispatch<React.SetStateAction<SyncStatus>>;
  showConfirmModal: (config: ConfirmModalConfig) => void;
}

interface AccessZone {
  id: string;
  name: string;
  zone_type: string;
  entry_type?: string;  // "entrada", "passagem", "saida"
  source: string;       // "direct", "person_type", "department", "unit"
  profile_name: string;
  profile_id: string;
  access_type?: string; // "allow" | "deny"
  devices?: {
    id: string;
    name: string;
    device_type: string;
    is_synced?: boolean;  // Track if person is on this device
    removal_reason?: string;  // "entered", "revoked", "expired", null
    removed_at?: string;
  }[];
}

interface DeviceEnrollment {
  [deviceId: string]: {
    is_enrolled: boolean;
    removal_reason?: string | null;
    removed_at?: string | null;
  };
}

/** Text of the (i) beside "Zonas de Acesso" — see the heading for why it is a tooltip. */
const accessZonesNote = (isCompany: boolean) =>
  `As zonas de acesso são gerenciadas através de perfis atribuídos ao ${isCompany ? 'departamento' : 'unidade'}. ` +
  'Use a seção de Gestão de Acesso no menu principal para configurar.';

export const PermissionsTab: React.FC<PermissionsTabProps> = ({
  person,
  onRefresh,
  readOnly = false,
  darkMode = false,
  syncStatus,
  setSyncStatus,
  showConfirmModal
}) => {
  const { isSuperAdmin } = useKeycloak();
  const confirm = useConfirm();
  const { setData: setSyncProgressData, setRetryFn: setSyncRetryFn, pauseRef: syncPauseRef, stopRef: syncStopRef } = useSyncProgress();
  const [addingRole, setAddingRole] = useState(false);
  const [showDeviceIds, setShowDeviceIds] = useState(false);
  const [selectedRoleType, setSelectedRoleType] = useState<string>('');
  // A unit-based role carries its unit in role_metadata->>'unit_id'; without it the role grants
  // nothing (access_profile_service joins unit_profile_assignments on exactly that key). The
  // unit is therefore collected here, with the role, instead of in a second trip to Visão Geral.
  const [selectedUnit, setSelectedUnit] = useState<UnitOption | null>(null);
  const [selectedRelationType, setSelectedRelationType] = useState<string>('proprietario');
  const [showRoleHelp, setShowRoleHelp] = useState(false);
  const roleHelpAnchorRef = useRef<HTMLButtonElement>(null);
  const [roleHelpPos, setRoleHelpPos] = useState({ top: 0, left: 0 });

  // Units attached to an EXISTING role. The add form only covers roles being created; every
  // person already carrying a unit-less Morador needs to be fixable from here too.
  const [editingUnitsRoleId, setEditingUnitsRoleId] = useState<string | null>(null);
  const [editingUnitAssignments, setEditingUnitAssignments] = useState<RoleUnitAssignment[]>([]);
  const [savingUnits, setSavingUnits] = useState(false);

  // Departments, for staff roles on empresa customers — the department equivalent of the above.
  // A staff member can belong to several departments: the backend models that as one
  // PersonRole row PER department, each carrying a singular department_id — which is why the
  // staff group below renders one sub-row per department.
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [editingDeptRoleId, setEditingDeptRoleId] = useState<string | null>(null);
  const [editingDepartmentIds, setEditingDepartmentIds] = useState<string[]>([]);
  const [savingDepartment, setSavingDepartment] = useState(false);

  const toggleIn = (list: string[], id: string): string[] =>
    list.includes(id) ? list.filter(x => x !== id) : [...list, id];

  // Only "Sem Departamento" is filtered out: offering the bucket that grants nothing would let
  // an operator answer the question with the one value guaranteed not to work.
  const selectableDepartments = departments.filter(d => !isFallbackDepartment(d.name));

  useEffect(() => {
    const customerId = person.customer?.id;
    if (!customerId || person.customer?.type !== 'empresa') return;

    let cancelled = false;
    customerService.getDepartments(customerId)
      .then((response: any) => {
        if (cancelled) return;
        // The endpoint answers { data: [...] }; customerService unwraps one level already.
        const raw = response?.data?.data ?? response?.data ?? response;
        setDepartments(Array.isArray(raw) ? raw : []);
      })
      .catch(() => { if (!cancelled) setDepartments([]); });

    return () => { cancelled = true; };
  }, [person.customer?.id, person.customer?.type]);

  /** Positioned off the anchor because the tooltip is portalled out of the clipping card. */
  const toggleRoleHelp = () => {
    if (!showRoleHelp && roleHelpAnchorRef.current) {
      const rect = roleHelpAnchorRef.current.getBoundingClientRect();
      // Keep it on screen when the anchor sits near the right edge.
      const width = 352; // w-[22rem]
      setRoleHelpPos({
        top: rect.bottom + 8,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      });
    }
    setShowRoleHelp(v => !v);
  };
  const [accessZones, setAccessZones] = useState<AccessZone[]>([]);
  const [loadingZones, setLoadingZones] = useState(false);
  const [syncingZoneId, setSyncingZoneId] = useState<string | null>(null);
  const [deviceEnrollment, setDeviceEnrollment] = useState<DeviceEnrollment>({});
  const [selectedZoneForModal, setSelectedZoneForModal] = useState<AccessZone | null>(null);
  const [selectedDeviceForModal, setSelectedDeviceForModal] = useState<IoTDevice | null>(null);
  const [selectedDeviceZone, setSelectedDeviceZone] = useState<Zone | null>(null);

  // Cache of full device info (IP, credentials) for HYBRID operations — populated after zones load
  const deviceInfoCacheRef = useRef<Map<string, DeviceInfo>>(new Map());

  // Direct person assignments
  const [directAssignments, setDirectAssignments] = useState<any[]>([]);
  const [loadingDirectAssignments, setLoadingDirectAssignments] = useState(false);
  const [availableProfiles, setAvailableProfiles] = useState<{
    id: string;
    name: string;
    description?: string;
    applies_to?: string;
    zone_name?: string;
    zone_group_names?: string[];
  }[]>([]);
  const [showAddProfilePicker, setShowAddProfilePicker] = useState(false);
  const [selectedProfileToAdd, setSelectedProfileToAdd] = useState('');
  const [assigningProfile, setAssigningProfile] = useState(false);
  // Preview of what selecting a profile will actually grant — zones it covers and the
  // devices inside them — fetched on demand when the user picks a profile in the dropdown.
  const [profilePreview, setProfilePreview] = useState<{ zones: { id: string; name: string }[]; devices: { id: string; name: string; device_type?: string }[] } | null>(null);
  const [loadingProfilePreview, setLoadingProfilePreview] = useState(false);

  // Device communication hook for hybrid direct/cloud communication
  const deviceComm = useDeviceCommunication();

  // Available role types based on customer type
  const availableRoleTypes = availableRoleTypesFor(person.customer?.type);

  // Fetch access zones and direct assignments based on person's department or unit
  useEffect(() => {
    fetchAccessZones();
    fetchDirectAssignments();
  }, [person.id]);


  const needsUnit = isUnitBasedRole(selectedRoleType);
  const needsDepartment = isDepartmentBasedRole(selectedRoleType);
  /** A unit- or department-based role without one opens nothing. */
  const addRoleBlocked =
    !selectedRoleType
    || (needsUnit && !selectedUnit)
    || (needsDepartment && selectedDepartmentIds.length === 0);

  const resetAddRoleForm = () => {
    setAddingRole(false);
    setSelectedRoleType('');
    setSelectedUnit(null);
    setSelectedRelationType('proprietario');
    setSelectedDepartmentIds([]);
  };

  const handleAddRole = async () => {
    if (addRoleBlocked || !person.customer?.id) return;

    // unit_id / relation_type are folded into role_metadata by CreatePersonRole, so the role
    // and its unit are created in a single request — the role is never briefly live with no unit.
    const payload: Record<string, unknown> = { role_type: selectedRoleType };
    if (needsUnit && selectedUnit) {
      payload.unit_id = selectedUnit.value;
      payload.relation_type = selectedRelationType;
    }
    // department_id is read straight out of role_metadata by the access join, and CreatePersonRole
    // only falls back to "Sem Departamento" when it is absent — which is what used to happen.
    // It creates ONE row, so the first department goes here and any others follow via PATCH.
    if (needsDepartment && selectedDepartmentIds.length > 0) {
      payload.role_metadata = { department_id: selectedDepartmentIds[0] };
    }

    try {
      const created = await api.post(`/customers/${person.customer.id}/people/${person.id}/roles`, payload);

      // Extra departments need their own role rows, which only the department_ids PATCH
      // creates. Done after the POST so a failure here still leaves a valid role with one
      // department rather than no role at all.
      if (needsDepartment && selectedDepartmentIds.length > 1) {
        // CreatePersonRole answers { message, data: role }.
        const roleId = created?.data?.data?.id ?? created?.data?.id ?? created?.data?.role?.id;
        if (roleId) {
          await api.patch(
            `/customers/${person.customer.id}/people/${person.id}/roles/${roleId}/metadata`,
            { department_ids: selectedDepartmentIds },
          );
        }
      }

      const departmentNames = selectedDepartmentIds
        .map(id => departments.find(d => d.id === id)?.name)
        .filter(Boolean);
      toast.success(
        needsUnit && selectedUnit
          ? `Função adicionada na unidade ${selectedUnit.unitNumber || selectedUnit.label}`
          : departmentNames.length > 0
            ? `Função adicionada em ${departmentNames.join(', ')}`
            : 'Função adicionada',
      );
      resetAddRoleForm();
      onRefresh();
    } catch (err: any) {
      console.error('Failed to add role:', err);
      toast.error(err.response?.data?.error || 'Erro ao adicionar função');
    }
  };

  const startEditingUnits = (role: any) => {
    setEditingUnitsRoleId(role.id);
    setEditingUnitAssignments(readRoleUnits(role.role_metadata));
  };

  /**
   * Same endpoint and payload Visão Geral uses (OverviewTab), so both screens write identical
   * metadata — a unit set here is indistinguishable from one set there.
   */
  const handleSaveRoleUnits = async (roleId: string) => {
    if (!person.customer?.id) return;
    setSavingUnits(true);
    try {
      await api.patch(
        `/customers/${person.customer.id}/people/${person.id}/roles/${roleId}/metadata`,
        { unit_assignments: editingUnitAssignments },
      );
      toast.success(
        editingUnitAssignments.length === 0
          ? 'Unidades removidas da função'
          : `${editingUnitAssignments.length} unidade(s) salva(s)`,
      );
      setEditingUnitsRoleId(null);
      setEditingUnitAssignments([]);
      onRefresh();
    } catch (err: any) {
      console.error('Failed to save role units:', err);
      toast.error(err.response?.data?.error || 'Erro ao salvar unidades');
    } finally {
      setSavingUnits(false);
    }
  };

  /**
   * Seed the editor with EVERY department the person currently holds.
   *
   * department_ids is a full replacement: PatchPersonRoleMetadata deactivates the role row of
   * any department missing from the list. Opening with a partial selection would therefore
   * quietly remove the departments that happened not to be shown.
   *
   * The fallback bucket is excluded — it grants nothing, and carrying it back in as a ticked
   * box invites saving it unchanged.
   */
  const startEditingDepartments = (staffRoles: any[]) => {
    setEditingDeptRoleId(staffRoles[0]?.id ?? null);
    setEditingDepartmentIds(
      staffRoles
        .filter(r => r.role_metadata?.department_id && !isFallbackDepartment(r.role_metadata?.department_name))
        .map(r => r.role_metadata.department_id),
    );
  };

  /** Same metadata PATCH the department columns on Employees/CondoEmployees use. */
  const handleSaveRoleDepartments = async (roleId: string) => {
    if (!person.customer?.id || editingDepartmentIds.length === 0) return;
    setSavingDepartment(true);
    try {
      await api.patch(
        `/customers/${person.customer.id}/people/${person.id}/roles/${roleId}/metadata`,
        { department_ids: editingDepartmentIds },
      );
      const names = editingDepartmentIds
        .map(id => departments.find(d => d.id === id)?.name)
        .filter(Boolean);
      toast.success(names.length ? `Departamentos: ${names.join(', ')}` : 'Departamentos salvos');
      setEditingDeptRoleId(null);
      setEditingDepartmentIds([]);
      onRefresh();
    } catch (err: any) {
      console.error('Failed to save role departments:', err);
      toast.error(err.response?.data?.error || 'Erro ao salvar departamentos');
    } finally {
      setSavingDepartment(false);
    }
  };

  const handleAuthorizeRole = async (roleId: string) => {
    if (!person.customer?.id) {
      toast.error('Erro: Customer ID não encontrado');
      return;
    }

    try {
      await api.post(`/customers/${person.customer.id}/people/${person.id}/roles/${roleId}/authorize`);
      toast.success('Função autorizada com sucesso!');

      // Auto-sync to devices in background (fire-and-forget)
      setSyncStatus(prev => ({ ...prev, isSyncing: true, syncMessage: 'Sincronizando com dispositivos...' }));
      const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;
      if (isElectronApp) {
        // ARCHITECTURE RULE: inside Electron, device sync is executed LOCALLY only —
        // never delegated to the cloud. A local failure is surfaced as a failure.
        (async () => {
          try {
            const allDeviceIds: string[] = [];
            const deviceNameMap = new Map<string, string>();
            accessZones.forEach(zone => {
              zone.devices?.forEach(device => {
                if (!allDeviceIds.includes(device.id)) allDeviceIds.push(device.id);
                deviceNameMap.set(device.id, device.name);
              });
            });

            if (allDeviceIds.length === 0) {
              setSyncStatus({
                isSyncing: false,
                lastSyncAt: new Date(),
                syncResult: 'success',
                syncMessage: 'Nenhum dispositivo para sincronizar',
                successfulDevices: [],
                failedDevices: [],
              });
              return;
            }

            const deviceDetails = await Promise.all(allDeviceIds.map(id => fetchDeviceDetails(id)));
            const validDevices = deviceDetails.filter((d): d is DeviceInfo => d !== null);

            if (validDevices.length === 0) {
              toast.error('Nenhum dispositivo disponível para sincronização local');
              setSyncStatus(prev => ({
                ...prev,
                isSyncing: false,
                syncResult: 'error',
                syncMessage: 'Nenhum dispositivo disponível para sincronização local',
              }));
              return;
            }

            const personSyncData: PersonData = {
              person_id: person.id,
              first_name: person.first_name,
              last_name: person.last_name,
              cpf: person.cpf,
              identity_photo_url: person.identity_photo_url,
              profile_picture_url: person.profile_picture_url,
              isAllowed: true,
              rfid_tags: (person.vehicles || []).map(v => v.rfid_tag).filter((t): t is string => !!t),
              vehicle_plates: (person.vehicles || []).map(v => v.plate).filter((p): p is string => !!p),
            };

            const results = await Promise.all(validDevices.map(deviceInfo =>
              DeviceIntegration.instance.execute(deviceInfo, DeviceOperations.SYNC_PERSON, personSyncData, CommunicationMode.HYBRID)
            ));

            const successfulDevices: { device_id: string; device_name: string }[] = [];
            const failedDevices: { device_id: string; device_name: string; error: string }[] = [];
            results.forEach((r, i) => {
              const d = validDevices[i];
              const name = deviceNameMap.get(d.id) || d.id;
              if (r.success) {
                successfulDevices.push({ device_id: d.id, device_name: name });
                // Audit trail + reconciliation ledger (no device call — Electron already did it).
                reportDeviceSyncOutcome({
                  customerId: person.customer!.id, personId: person.id, deviceId: d.id, device: d,
                  success: true, source: r.source, durationMs: r.latencyMs,
                });
              } else {
                failedDevices.push({ device_id: d.id, device_name: name, error: r.error || 'Falha' });
                reportDeviceSyncOutcome({
                  customerId: person.customer!.id, personId: person.id, deviceId: d.id, device: d,
                  success: false, source: r.source, durationMs: r.latencyMs, errorMessage: r.error,
                });
              }
            });

            const failedCount = failedDevices.length + (allDeviceIds.length - validDevices.length);
            setSyncStatus({
              isSyncing: false,
              lastSyncAt: new Date(),
              syncResult: successfulDevices.length === 0 ? 'error' : failedCount > 0 ? 'partial' : 'success',
              syncMessage: successfulDevices.length === 0
                ? 'Falha ao sincronizar dispositivos localmente'
                : failedCount > 0
                  ? `Sincronizado parcialmente: ${successfulDevices.length}/${allDeviceIds.length} dispositivo(s)`
                  : `Sincronizado com ${successfulDevices.length} dispositivo(s)`,
              successfulDevices,
              failedDevices,
            });

            if (successfulDevices.length === 0) {
              toast.error('Falha ao sincronizar dispositivos localmente');
            } else if (failedCount > 0) {
              toast(`Sincronizado parcialmente: ${failedCount} dispositivo(s) falharam`);
            }
          } catch (err) {
            console.error('Auto-sync failed (local):', err);
            toast.error('Erro na sincronização automática local');
            setSyncStatus(prev => ({
              ...prev,
              isSyncing: false,
              syncResult: 'error',
              syncMessage: 'Erro na sincronização automática',
            }));
          }
        })();
      } else {
        // Web browser (non-Electron): the backend executes the sync via cloud.
        api.post(`/customers/${person.customer.id}/people/${person.id}/sync`)
          .then((response) => {
            const result = response.data;
            const devicesSynced = result.devices_synced || 0;
            const failedDevices = result.failed_devices || [];

            setSyncStatus({
              isSyncing: false,
              lastSyncAt: new Date(),
              syncResult: failedDevices.length > 0 ? 'partial' : 'success',
              syncMessage: failedDevices.length > 0
                ? `Sincronizado parcialmente: ${devicesSynced} dispositivo(s)`
                : `Sincronizado com ${devicesSynced} dispositivo(s)`,
              successfulDevices: result.successful_devices || [],
              failedDevices: failedDevices,
            });

            if (failedDevices.length > 0) {
              toast(`Sincronizado parcialmente: ${failedDevices.length} dispositivos falharam`);
            }
          })
          .catch(err => {
            console.error('Auto-sync failed (non-critical):', err);
            setSyncStatus(prev => ({
              ...prev,
              isSyncing: false,
              syncResult: 'error',
              syncMessage: 'Erro na sincronização automática'
            }));
          });
      }

      onRefresh();
    } catch (err: any) {
      console.error('Failed to authorize role:', err);
      toast.error(err.response?.data?.error || 'Erro ao autorizar função');
    }
  };

  // Executes HYBRID revoke-all for the person across all zone devices.
  // Returns counts so callers can decide whether to proceed.
  const executeRevokeAll = async (): Promise<{ successCount: number; failedCount: number }> => {
    const seenDeviceIds = new Set<string>();
    const allDevices: { id: string; name: string }[] = [];
    accessZones.forEach(zone => {
      zone.devices?.forEach(device => {
        if (!seenDeviceIds.has(device.id)) {
          seenDeviceIds.add(device.id);
          allDevices.push({ id: device.id, name: device.name });
        }
      });
    });

    if (allDevices.length === 0) return { successCount: 0, failedCount: 0 };

    const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;
    const deviceDetailsMap = new Map<string, DeviceInfo>(deviceInfoCacheRef.current);

    if (isElectronApp) {
      const uncached = allDevices.filter(d => !deviceDetailsMap.has(d.id));
      if (uncached.length > 0) {
        const results = await Promise.allSettled(uncached.map(d => fetchDeviceDetails(d.id)));
        results.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value) deviceDetailsMap.set(uncached[i].id, r.value);
        });
      }
    }

    let successCount = 0;
    let failedCount = 0;
    const toastId = notify.loading('Revogando acesso', `0 de ${allDevices.length} dispositivos`);
    let processed = 0;

    for (const device of allDevices) {
      const deviceStart = Date.now();
      try {
        const deviceInfo = deviceDetailsMap.get(device.id);
        let directFailed = false;
        if (isElectronApp && deviceInfo && (deviceInfo.local_ip_address || deviceInfo.ip_address)) {
          try {
            const result = await DeviceIntegration.instance.execute(deviceInfo, DeviceOperations.REMOVE_PERSON, { person_id: person.id }, CommunicationMode.HYBRID);
            console.log(`[RevokeAll] ${result.success ? '' : ''} ${device.name}: success=${result.success}`);
            if (!result.success) directFailed = true;
          } catch (directErr) {
            console.warn(`[RevokeAll] HYBRID failed for ${device.name}:`, directErr);
            directFailed = true;
          }
        }
        if (directFailed) {
          failedCount++;
        } else {
          if (isElectronApp) {
            await api.patch(`/customers/${person.customer!.id}/people/${person.id}/devices/${device.id}/mark-revoked`);
          } else {
            await api.delete(`/customers/${person.customer!.id}/people/${person.id}/devices/${device.id}/revoke`);
          }
          successCount++;
        }
      } catch (err) {
        console.warn(`[RevokeAll] Failed for ${device.name}:`, err);
        failedCount++;
      }
      processed++;
      notify.loading('Revogando acesso', `${processed} de ${allDevices.length} dispositivos`, { id: toastId });
      const elapsed = Date.now() - deviceStart;
      if (elapsed < 500) await new Promise(r => setTimeout(r, 500 - elapsed));
    }

    toast.dismiss(toastId);
    return { successCount, failedCount };
  };

  const handleToggleRoleActive = async (roleId: string, currentIsActive: boolean) => {
    if (!person.customer?.id) {
      toast.error('Erro: Customer ID não encontrado');
      return;
    }

    const newIsActive = !currentIsActive;

    // Activating — no revoke needed
    if (newIsActive) {
      try {
        await api.put(`/customers/${person.customer.id}/people/${person.id}/roles/${roleId}`, { is_active: true });
        toast.success('Função ativada com sucesso!');
        onRefresh();
      } catch (err: any) {
        toast.error(err.response?.data?.error || 'Erro ao ativar função');
      }
      return;
    }

    // Deactivating — confirm + revoke first
    const performDeactivation = async () => {
      try {
        await api.put(`/customers/${person.customer!.id}/people/${person.id}/roles/${roleId}`, { is_active: false });
        toast.success('Função desativada com sucesso!');
        onRefresh();
      } catch (err: any) {
        toast.error(err.response?.data?.error || 'Erro ao desativar função');
      }
    };

    showConfirmModal({
      title: 'Desativar Função',
      message: 'Desativar esta função irá revogar o acesso físico da pessoa em todos os dispositivos associados. Deseja continuar?',
      confirmText: 'Desativar e Revogar Acesso',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      icon: 'revoke',
      onConfirm: async () => {
        const { successCount, failedCount } = await executeRevokeAll();

        if (failedCount > 0) {
          // Some devices failed — warn about security risk before proceeding
          showConfirmModal({
            title: 'Risco de Segurança',
            message: `${failedCount} dispositivo(s) não puderam ser revogados. A pessoa pode ainda ter acesso físico nesses dispositivos.\n\nDeseja continuar a desativação mesmo assim?`,
            confirmText: 'Continuar mesmo assim',
            confirmButtonClass: 'bg-orange-600 hover:bg-orange-700',
            icon: 'warning',
            onConfirm: performDeactivation,
          });
        } else {
          await performDeactivation();
        }
      },
    });
  };

  const fetchAccessZones = async () => {
    if (!person.customer?.id || !person.id) return;

    setLoadingZones(true);
    try {
      // Call the new endpoint that resolves all access sources for a person
      const response = await api.get(
        `/customers/${person.customer.id}/people/${person.id}/effective-access-zones`
      );

      const zones: AccessZone[] = response.data.zones || [];
      setAccessZones(zones);

      // Fetch device enrollment status for all devices
      await fetchDeviceEnrollmentStatus(zones);

      // Pre-fetch and cache full device credentials for HYBRID operations (Electron only).
      // Done in the background — failures are silently ignored.
      if (typeof window !== 'undefined' && window.electronAPI) {
        const seenIds = new Set<string>();
        const deviceIds: string[] = [];
        zones.forEach(z => z.devices?.forEach(d => { if (!seenIds.has(d.id)) { seenIds.add(d.id); deviceIds.push(d.id); } }));
        Promise.allSettled(deviceIds.map(id => fetchDeviceDetails(id))).then(results => {
          results.forEach((r, i) => {
            if (r.status === 'fulfilled' && r.value) {
              deviceInfoCacheRef.current.set(deviceIds[i], r.value);
            }
          });
          console.log(`[PermissionsTab] Cached ${deviceInfoCacheRef.current.size}/${deviceIds.length} device credentials for HYBRID`);
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch access zones:', err);
      setAccessZones([]);
    } finally {
      setLoadingZones(false);
    }
  };

  const fetchDirectAssignments = async () => {
    if (!person.id) return;

    setLoadingDirectAssignments(true);
    try {
      const response = await api.get(`/access-profiles/person/${person.id}/assignments`);

      // This section is specifically "diretas" (per-person exceptions) — person_type-based
      // assignments (e.g. "all Moradores") apply broadly to the whole customer, not to this
      // person individually, so they don't belong here and are excluded.
      const directAssignments = response.data.direct_assignments || [];
      setDirectAssignments(directAssignments.map((a: any) => ({ ...a, assignment_type: 'direct' })));
    } catch (err: any) {
      console.error('Failed to fetch direct assignments:', err);
      setDirectAssignments([]);
    } finally {
      setLoadingDirectAssignments(false);
    }
  };

  const fetchAvailableProfiles = async () => {
    if (!person.customer?.id) return;
    try {
      const response = await api.get(`/access-profiles/customer/${person.customer.id}`);
      setAvailableProfiles(response.data.profiles || []);
    } catch (err: any) {
      console.error('Failed to fetch access profiles:', err);
      toast.error('Falha ao carregar perfis de acesso');
    }
  };

  // Resolves a profile to the zones it covers and the devices inside those zones, so the
  // operator can see exactly what they're about to grant before confirming.
  const handleSelectProfileToAdd = async (profileId: string) => {
    setSelectedProfileToAdd(profileId);
    setProfilePreview(null);
    if (!profileId) return;

    setLoadingProfilePreview(true);
    try {
      const zonesResp = await api.get(`/access-profiles/${profileId}/zones`);
      const zones: { id: string; name: string }[] = zonesResp.data.zones || [];

      const deviceLists = await Promise.all(
        zones.map(z => api.get(`/zones/${z.id}/devices`).then(r => r.data.data || r.data.devices || []).catch(() => []))
      );
      const deviceMap = new Map<string, { id: string; name: string; device_type?: string }>();
      deviceLists.flat().forEach((d: any) => {
        if (d?.id && !deviceMap.has(d.id)) {
          deviceMap.set(d.id, { id: d.id, name: d.name, device_type: d.device_type });
        }
      });

      setProfilePreview({ zones, devices: Array.from(deviceMap.values()) });
    } catch (err) {
      console.error('Failed to load profile preview:', err);
      setProfilePreview({ zones: [], devices: [] });
    } finally {
      setLoadingProfilePreview(false);
    }
  };

  // Assign a profile directly to this person — same mechanism as "Exceções — adicionar
  // individualmente" on the Perfis de Acesso page, just scoped from the person's own view.
  const handleAssignProfileDirect = async () => {
    if (!selectedProfileToAdd || !person.customer?.id) return;

    setAssigningProfile(true);
    try {
      await api.post('/access-profiles/bulk-assign', {
        customer_id: person.customer.id,
        access_profile_id: selectedProfileToAdd,
        person_types: [],
        departments: [],
        units: [],
        exception_person_ids: [person.id],
      });
      toast.success('Perfil atribuído com sucesso');
      setShowAddProfilePicker(false);
      setSelectedProfileToAdd('');
      setProfilePreview(null);
      fetchDirectAssignments();
      fetchAccessZones();
      onRefresh();
    } catch (err: any) {
      console.error('Failed to assign profile:', err);
      toast.error(err.response?.data?.error || 'Erro ao atribuir perfil');
    } finally {
      setAssigningProfile(false);
    }
  };

  const handleRemoveDirectAssignment = async (assignmentId: string, profileName: string) => {
    if (!await confirm({ message: `Tem certeza que deseja remover a atribuição direta do perfil "${profileName}"?`, variant: 'danger', confirmLabel: 'Remover' })) {
      return;
    }

    try {
      await api.delete(`/access-profiles/assignments/${assignmentId}`);
      toast.success('Atribuição direta removida com sucesso');
      fetchDirectAssignments();
      fetchAccessZones();
      onRefresh();
    } catch (err: any) {
      console.error('Failed to remove direct assignment:', err);
      toast.error(err.response?.data?.error || 'Erro ao remover atribuição');
    }
  };

  // Fetch which devices already have this person enrolled
  const fetchDeviceEnrollmentStatus = async (zones: AccessZone[]) => {
    if (!person.customer?.id || !person.id) return;

    try {
      // Get all unique device IDs from zones
      const deviceIds: string[] = [];
      zones.forEach(zone => {
        zone.devices?.forEach(device => {
          if (!deviceIds.includes(device.id)) {
            deviceIds.push(device.id);
          }
        });
      });

      if (deviceIds.length === 0) return;

      // Fetch enrollment status for each device
      const enrollmentStatus: DeviceEnrollment = {};

      // Make parallel requests to check enrollment on each device
      await Promise.all(
        deviceIds.map(async (deviceId) => {
          try {
            const response = await api.get(
              `/customers/${person.customer!.id}/people/${person.id}/devices/${deviceId}/enrollment-status`
            );
            enrollmentStatus[deviceId] = {
              is_enrolled: response.data.is_enrolled || false,
              removal_reason: response.data.removal_reason || null,
              removed_at: response.data.removed_at || null,
            };
          } catch (err) {
            // If endpoint doesn't exist or fails, assume not enrolled
            enrollmentStatus[deviceId] = {
              is_enrolled: false,
              removal_reason: null,
              removed_at: null,
            };
          }
        })
      );

      setDeviceEnrollment(enrollmentStatus);

      // Update zones with enrollment status
      const updatedZones = zones.map(zone => ({
        ...zone,
        devices: zone.devices?.map(device => ({
          ...device,
          is_synced: enrollmentStatus[device.id]?.is_enrolled || false,
          removal_reason: enrollmentStatus[device.id]?.removal_reason || null,
          removed_at: enrollmentStatus[device.id]?.removed_at || null,
        })),
      }));

      setAccessZones(updatedZones);
    } catch (err) {
      console.error('Failed to fetch device enrollment status:', err);
    }
  };

  // Sync this person to all their authorized devices
  /** Templates the fingerprint readers need; absent ones are simply an empty list. */
  const fetchFingerprintTemplates = async (): Promise<{ templateB64: string }[]> => {
    if (!person.customer?.id || !person.id) return [];
    try {
      const res = await api.get(`/customers/${person.customer.id}/people/${person.id}/fingerprints`);
      const fps: any[] = res.data?.fingerprints || res.data || [];
      return fps.filter((fp: any) => !!fp.template_b64).map((fp: any) => ({ templateB64: fp.template_b64 }));
    } catch {
      return []; // never block a sync on the fingerprint lookup
    }
  };

  const handleSyncAllZones = async () => {
    if (!person.customer?.id || !person.id) return;

    const personName = `${person.first_name} ${person.last_name}`;

    showConfirmModal({
      title: 'Sincronizar Todos os Dispositivos',
      message: `Sincronizar ${personName} com todos os dispositivos autorizados? Esta operação enviará as informações e permissões atualizadas para todos os dispositivos de controle de acesso.`,
      confirmText: 'Sincronizar Todos',
      confirmButtonClass: 'bg-blue-600 hover:bg-blue-700',
      icon: 'warning',
      showDisclaimer: true,
      disclaimerType: 'general',
      onConfirm: async () => {
        setSyncStatus(prev => ({ ...prev, isSyncing: true, syncResult: null, syncMessage: '' }));

        try {
          // Collect all devices from all zones
          const allDeviceIds: string[] = [];
          const deviceNameMap = new Map<string, string>();
          accessZones.forEach(zone => {
            zone.devices?.forEach(device => {
              if (!allDeviceIds.includes(device.id)) {
                allDeviceIds.push(device.id);
              }
              deviceNameMap.set(device.id, device.name);
            });
          });

          if (allDeviceIds.length === 0) {
            toast.error('Nenhum dispositivo encontrado');
            setSyncStatus(prev => ({ ...prev, isSyncing: false }));
            return;
          }

          // Prepare person data for sync (vendor-agnostic format)
          // Adapters will format this data according to each vendor's requirements
          // One builder for every sync button — see domain/personSyncData. Fingerprint
          // templates used to ride along ONLY with "Sincronizar RFID", so which button the
          // operator pressed decided whether a fingerprint reader got anything usable.
          const personData = buildPersonSyncPayload(
            person as any,
            await fetchFingerprintTemplates(),
          ) as PersonData;

          console.log(' [Sync] Person data (vendor-agnostic):', {
            personId: person.id,
            name: `${person.first_name} ${person.last_name}`,
            hasPhoto: !!(person.identity_photo_url || person.profile_picture_url),
          });

          // Attempt to fetch device details for hybrid communication
          console.log(' [Sync All] Starting device detail fetch for', allDeviceIds.length, 'devices');
          const deviceDetailsPromises = allDeviceIds.map(id => fetchDeviceDetails(id));
          const deviceDetails = await Promise.all(deviceDetailsPromises);
          const validDevices = deviceDetails.filter((d): d is DeviceInfo => d !== null);

          console.log(' [Sync All] Device details summary:', {
            totalDevices: allDeviceIds.length,
            validDevices: validDevices.length,
            isElectron: typeof window !== 'undefined' && !!window.electronAPI,
            hasDeviceHttpRequest: typeof window !== 'undefined' && !!window.electronAPI?.deviceHttpRequest,
          });

          // Sequential sync with 500ms minimum delay per device.
          // Devices with direct IPs use HYBRID (local → public).
          // Devices without IPs (cloud-only) are batched for the cloud API.
          const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;
          const validDeviceIds = new Set(validDevices.map(d => d.id));
          // Devices where fetchDeviceDetails failed go straight to cloud
          const cloudFallbackIds: string[] = allDeviceIds.filter(id => !validDeviceIds.has(id));
          const successfulDevices: { device_id: string; device_name: string }[] = [];
          const failedDevices: { device_id: string; device_name: string; error: string }[] = [];
          let localSuccessCount = 0;
          let cloudSuccessCount = 0;

          // Open sync progress page
          const progressItems: SyncProgressItem[] = [];
          const progressTitle = buildPersonSyncTitle(personName, allDeviceIds.length);
          const personPhotoUrl = person.identity_photo_url || person.profile_picture_url || undefined;
          // Every row is this person — the device name is only the row's title.
          const syncSubject = { id: person.id, name: personName, photoUrl: personPhotoUrl };
          setSyncProgressData({ zoneName: progressTitle, personPhotoUrl, total: allDeviceIds.length, done: 0, ok: 0, fail: 0, recentItems: [], isComplete: false });

          let processed = 0;
          let progressOk = 0;
          let progressFail = 0;

          /**
           * A device the desktop app could not reach is a failure NOW, not after the loop.
           * Deferring it left the modal showing "2 / 6 itens · Sucesso 0 · Falhas 0" over an
           * empty list: the bar moved while nothing was ever counted or named.
           */
          const markLocalFailure = (deviceId: string, name: string) => {
            failedDevices.push({ device_id: deviceId, device_name: name, error: LOCAL_UNREACHABLE });
            progressFail++;
            progressItems.push(buildPersonSyncItem({
              person: syncSubject, deviceName: name, status: 'failed', error: LOCAL_UNREACHABLE,
            }));
          };

          for (const deviceInfo of validDevices) {
            // Pause / stop support
            while (syncPauseRef.current) {
              await new Promise(r => setTimeout(r, 200));
            }
            if (syncStopRef.current) break;

            const deviceStart = Date.now();
            const deviceName = deviceNameMap.get(deviceInfo.id) || deviceInfo.id;
            const hasDirectIp = !!(deviceInfo.local_ip_address || deviceInfo.ip_address);

            if (isElectronApp && hasDirectIp) {
              try {
                // Executes, records the outcome in BOTH the audit trail and the
                // reconciliation ledger, and classifies. See utils/syncDeviceAndReport.
                const outcome = await syncDeviceAndReport({
                  customerId: person.customer!.id,
                  personId: person.id,
                  device: deviceInfo,
                  payload: personData,
                  deviceName,
                });
                const result = outcome.result;
                if (outcome.synced) {
                  successfulDevices.push({ device_id: deviceInfo.id, device_name: deviceName });
                  localSuccessCount++;
                  progressOk++;
                  progressItems.push(buildPersonSyncItem({ person: syncSubject, deviceName, status: 'ok' }));
                  // ControlID: assign user to default group after direct sync
                  if (deviceInfo.vendor === 'controlid' && (result.source === 'local' || result.source === 'public')) {
                    try {
                      const groupId = await DeviceIntegration.instance.ensureControlIDDefaultAccess(deviceInfo);
                      if (groupId !== null) {
                        const { assigned, skipped } = await DeviceIntegration.instance.assignAllDeviceUsersToGroup(deviceInfo, groupId);
                        console.log(`[PermSyncAll] ControlID group: device=${deviceInfo.id}, assigned=${assigned}, skipped=${skipped}`);
                      }
                    } catch (groupErr: any) {
                      console.warn(`[PermSyncAll] Group assign failed for ${deviceInfo.id}:`, groupErr?.message);
                    }
                  }
                } else {
                  // Electron never delegates to the cloud (see the rule below) — record the
                  // failure right here so the list and the counters move together.
                  markLocalFailure(deviceInfo.id, deviceName);
                }
              } catch {
                markLocalFailure(deviceInfo.id, deviceName);
              }
            } else if (isElectronApp) {
              // Electron, no reachable address — a failure, not a cloud candidate.
              markLocalFailure(deviceInfo.id, deviceName);
            } else {
              // Browser — the cloud batch below is the only path there is.
              cloudFallbackIds.push(deviceInfo.id);
            }

            processed++;
            setSyncProgressData({ zoneName: progressTitle, personPhotoUrl, total: allDeviceIds.length, done: processed, ok: progressOk, fail: progressFail, recentItems: [...progressItems], isComplete: false });
            const elapsed = Date.now() - deviceStart;
            if (elapsed < 500) await new Promise(r => setTimeout(r, 500 - elapsed));
          }

          // ARCHITECTURE RULE: in Electron, sync is executed LOCALLY only — devices
          // that could not be reached locally are reported as failures, never
          // delegated to the cloud. The cloud batch below is the web-browser arm.
          if (cloudFallbackIds.length > 0 && isElectronApp) {
            // Only devices whose details could not be fetched at all reach this point now;
            // everything attempted in the loop was already settled by markLocalFailure.
            cloudFallbackIds.forEach(id => {
              markLocalFailure(id, deviceNameMap.get(id) || id);
            });
          } else if (cloudFallbackIds.length > 0) {
            console.log('[Sync All] Cloud API for', cloudFallbackIds.length, 'device(s)');
            try {
              const cloudResponse = await api.post(
                `/customers/${person.customer!.id}/people/${person.id}/sync`,
                { device_ids: cloudFallbackIds }
              );
              const cloudResult = cloudResponse.data;
              const cloudSuccessful: any[] = cloudResult.successful_devices || [];
              const cloudFailed: any[] = cloudResult.failed_devices || [];

              cloudSuccessful.forEach((d: any) => {
                // Prefer zone display name over what the API returns
                const name = deviceNameMap.get(d.device_id) || d.device_name || d.device_id;
                successfulDevices.push({ device_id: d.device_id, device_name: name });
                cloudSuccessCount++;
                progressOk++;
                progressItems.push(buildPersonSyncItem({ person: syncSubject, deviceName: name, status: 'ok' }));
                // Audit row only: the cloud sync path records device_credential_states
                // server-side from iot-connect's own per-device response.
                reportDeviceSyncOutcome({
                  customerId: person.customer!.id, personId: person.id, deviceId: d.device_id,
                  success: true, source: 'cloud', recordLedger: false,
                });
              });
              cloudFailed.forEach((d: any) => {
                const name = deviceNameMap.get(d.device_id) || d.device_name || d.device_id;
                failedDevices.push({
                  device_id: d.device_id,
                  device_name: name,
                  error: d.error || 'Falha na nuvem',
                });
                progressFail++;
                progressItems.push(buildPersonSyncItem({ person: syncSubject, deviceName: name, status: 'failed', error: d.error || 'Falha na nuvem' }));
              });

              // If the API returned neither array, use devices_synced count
              if (cloudSuccessful.length === 0 && cloudFailed.length === 0) {
                const cloudSynced = cloudResult.devices_synced || 0;
                cloudFallbackIds.slice(0, cloudSynced).forEach(id => {
                  const name = deviceNameMap.get(id) || id;
                  successfulDevices.push({ device_id: id, device_name: name });
                  cloudSuccessCount++;
                  progressOk++;
                  progressItems.push(buildPersonSyncItem({ person: syncSubject, deviceName: name, status: 'ok' }));
                });
                cloudFallbackIds.slice(cloudSynced).forEach(id => {
                  const name = deviceNameMap.get(id) || id;
                  failedDevices.push({ device_id: id, device_name: name, error: 'Falha na nuvem' });
                  progressFail++;
                  progressItems.push(buildPersonSyncItem({ person: syncSubject, deviceName: name, status: 'failed', error: 'Falha na nuvem' }));
                });
              }
            } catch (cloudErr: any) {
              cloudFallbackIds.forEach(id => {
                const name = deviceNameMap.get(id) || id;
                const errMsg = cloudErr.response?.data?.error || 'Falha na nuvem';
                failedDevices.push({ device_id: id, device_name: name, error: errMsg });
                progressFail++;
                progressItems.push(buildPersonSyncItem({ person: syncSubject, deviceName: name, status: 'failed', error: errMsg }));
              });
            }
          }

          // Mark progress complete
          setSyncProgressData({
            zoneName: progressTitle,
            personPhotoUrl,
            total: allDeviceIds.length,
            done: allDeviceIds.length,
            ok: progressOk,
            fail: progressFail,
            recentItems: [...progressItems],
            isComplete: true,
          });

          // Determine overall result
          const totalSuccess = successfulDevices.length;
          const totalFailed = failedDevices.length;
          const totalDevices = allDeviceIds.length;
          let syncResult: 'success' | 'error' | 'partial' = 'success';
          let syncMessage = '';

          // Build source breakdown annotation (e.g. "2 local, 1 nuvem")
          const sourceAnnotation = (() => {
            if (localSuccessCount > 0 && cloudSuccessCount > 0) return ` (${localSuccessCount} local, ${cloudSuccessCount} nuvem)`;
            if (localSuccessCount > 0) return ` (${localSuccessCount} local)`;
            if (cloudSuccessCount > 0) return ` (${cloudSuccessCount} nuvem)`;
            return '';
          })();

          if (totalFailed > 0 && totalSuccess === 0) {
            syncResult = 'error';
            syncMessage = `Falha ao sincronizar ${totalDevices} dispositivo(s)`;
            toast.error(syncMessage, { duration: 5000 });
          } else if (totalFailed > 0) {
            syncResult = 'partial';
            syncMessage = `Sincronizado parcialmente: ${totalSuccess} de ${totalDevices} dispositivo(s)${sourceAnnotation}`;
            toast(syncMessage, { duration: 5000 });
          } else {
            syncResult = 'success';
            syncMessage = `${totalSuccess} dispositivo(s) sincronizado(s)${sourceAnnotation}`;
            toast.success(syncMessage, { duration: 4000 });
          }

          setSyncStatus({
            isSyncing: false,
            lastSyncAt: new Date(),
            syncResult,
            syncMessage,
            successfulDevices,
            failedDevices,
          });

          fetchAccessZones();
        } catch (err: any) {
          console.error('Failed to sync person:', err);
          toast.error(err.response?.data?.error || 'Erro ao sincronizar');
          setSyncStatus({
            isSyncing: false,
            lastSyncAt: new Date(),
            syncResult: 'error',
            syncMessage: err.response?.data?.error || 'Erro ao sincronizar',
            successfulDevices: [],
            failedDevices: [],
          });
        }
      },
    });
  };

  // Helper: Fetch full device details with credentials (for hybrid direct communication)
  const fetchDeviceDetails = async (deviceId: string): Promise<DeviceInfo | null> => {
    try {
      console.log(' [Fetch Device] Getting details for device:', deviceId);
      const response = await api.get(`/iot-devices/${deviceId}`);
      const device = response.data;

      const deviceInfo = apiDeviceToDeviceInfo(device);

      console.log(' [Fetch Device] Got device details:', {
        id: deviceInfo.id,
        hasLocalIp: !!deviceInfo.local_ip_address,
        hasPublicIp: !!deviceInfo.ip_address,
        hasUsername: !!deviceInfo.username,
        hasPassword: !!deviceInfo.password,
        vendor: deviceInfo.vendor,
      });

      return deviceInfo;
    } catch (err: any) {
      console.warn(' [Fetch Device] Failed to fetch device details:', deviceId, err);
      return null;
    }
  };

  // Sync a specific zone (syncs this person to all devices including this zone)
  // Now supports hybrid direct/cloud communication when running in Electron
  const handleSyncZone = async (zoneId: string, zoneName?: string) => {
    if (!person.customer?.id || !person.id) return;

    const personName = `${person.first_name} ${person.last_name}`;
    const zoneLabel = zoneName || 'esta zona';

    showConfirmModal({
      title: 'Sincronizar Zona',
      message: `Sincronizar ${personName} com todos os dispositivos em "${zoneLabel}"? Esta operação atualizará as permissões nos dispositivos desta zona.`,
      confirmText: 'Sincronizar',
      confirmButtonClass: 'bg-blue-600 hover:bg-blue-700',
      icon: 'warning',
      showDisclaimer: true,
      disclaimerType: 'general',
      onConfirm: async () => {
        setSyncingZoneId(zoneId);

        try {
          // Find the zone and get its devices
          const zone = accessZones.find(z => z.id === zoneId);
          if (!zone || !zone.devices || zone.devices.length === 0) {
            toast.error('Nenhum dispositivo encontrado nesta zona');
            setSyncingZoneId(null);
            return;
          }

          // Try hybrid direct communication (Electron only)
          // Fallback to cloud API if:
          // - Running in web browser
          // - Device details fetch fails
          // - Direct communication fails
          const deviceIds = zone.devices.map(d => d.id);

          // Attempt to fetch device details for hybrid communication
          console.log(' [Zone Sync] Starting device detail fetch for zone:', zoneLabel);
          const deviceDetailsPromises = deviceIds.map(id => fetchDeviceDetails(id));
          const deviceDetails = await Promise.all(deviceDetailsPromises);
          const validDevices = deviceDetails.filter((d): d is DeviceInfo => d !== null);

          console.log(' [Zone Sync] Device details summary:', {
            totalDevices: deviceIds.length,
            validDevices: validDevices.length,
            isElectron: typeof window !== 'undefined' && !!window.electronAPI,
            hasDeviceHttpRequest: typeof window !== 'undefined' && !!window.electronAPI?.deviceHttpRequest,
          });

          // Prepare person data for sync (vendor-agnostic format)
          // Adapters will format this data according to each vendor's requirements
          const personData: PersonData = {
            person_id: person.id,
            first_name: person.first_name,
            last_name: person.last_name,
            cpf: person.cpf,
            identity_photo_url: person.identity_photo_url,
            profile_picture_url: person.profile_picture_url,
            isAllowed: true,
            rfid_tags: (person.vehicles || []).map(v => v.rfid_tag).filter((t): t is string => !!t),
            vehicle_plates: (person.vehicles || []).map(v => v.plate).filter((p): p is string => !!p),
          };

          console.log(' [Sync] Person data (vendor-agnostic):', {
            personId: person.id,
            name: `${person.first_name} ${person.last_name}`,
            hasPhoto: !!(person.identity_photo_url || person.profile_picture_url),
          });

          // Sequential sync with 500ms minimum delay per device.
            const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;
            const validDeviceIds = new Set(validDevices.map(d => d.id));
            // Devices where fetchDeviceDetails failed go straight to cloud
            const cloudFallbackIds: string[] = deviceIds.filter(id => !validDeviceIds.has(id));
            const zoneSyncSuccessful: { device_id: string; device_name: string }[] = [];
            const zoneSyncFailed: { device_id: string; device_name: string; error: string }[] = [];
            let zoneLocalSuccessCount = 0;
            let zoneCloudSuccessCount = 0;

            // Open progress page
            const zoneProgressItems: SyncProgressItem[] = [];
            const zoneProgressTitle = `${personName} → ${zoneLabel}`;
            const zonePersonPhotoUrl = person.identity_photo_url || person.profile_picture_url || undefined;
            const zoneSyncSubject = { id: person.id, name: personName, photoUrl: zonePersonPhotoUrl };
            setSyncProgressData({ zoneName: zoneProgressTitle, personPhotoUrl: zonePersonPhotoUrl, total: deviceIds.length, done: 0, ok: 0, fail: 0, recentItems: [], isComplete: false });
            let zoneProgressOk = 0;
            let zoneProgressFail = 0;
            let processed = 0;

            /** Same rule as the sync-all path: an unreachable device fails immediately. */
            const markZoneLocalFailure = (deviceId: string, name: string) => {
              zoneSyncFailed.push({ device_id: deviceId, device_name: name, error: LOCAL_UNREACHABLE });
              zoneProgressFail++;
              zoneProgressItems.push(buildPersonSyncItem({
                person: zoneSyncSubject, deviceName: name, status: 'failed', error: LOCAL_UNREACHABLE,
              }));
            };

            for (const deviceInfo of validDevices) {
              // Pause / stop support
              while (syncPauseRef.current) {
                await new Promise(r => setTimeout(r, 200));
              }
              if (syncStopRef.current) break;

              const deviceStart = Date.now();
              const deviceName = zone.devices?.find(d => d.id === deviceInfo.id)?.name || deviceInfo.id;
              const hasDirectIp = !!(deviceInfo.local_ip_address || deviceInfo.ip_address);

              if (isElectronApp && hasDirectIp) {
                try {
                  const result = await DeviceIntegration.instance.execute(deviceInfo, DeviceOperations.SYNC_PERSON, personData, CommunicationMode.HYBRID);
                  if (result.success) {
                    zoneSyncSuccessful.push({ device_id: deviceInfo.id, device_name: deviceName });
                    zoneLocalSuccessCount++;
                    zoneProgressOk++;
                    zoneProgressItems.push(buildPersonSyncItem({ person: zoneSyncSubject, deviceName, status: 'ok' }));
                    // Audit trail + reconciliation ledger (fire-and-forget)
                    reportDeviceSyncOutcome({
                      customerId: person.customer!.id, personId: person.id, deviceId: deviceInfo.id,
                      device: deviceInfo, success: true, source: result.source, durationMs: result.latencyMs,
                    });
                    // ControlID: assign user to default group
                    if (deviceInfo.vendor === 'controlid' && (result.source === 'local' || result.source === 'public')) {
                      try {
                        const groupId = await DeviceIntegration.instance.ensureControlIDDefaultAccess(deviceInfo);
                        if (groupId !== null) {
                          const { assigned, skipped } = await DeviceIntegration.instance.assignAllDeviceUsersToGroup(deviceInfo, groupId);
                          console.log(`[PermSync] ControlID group: device=${deviceInfo.id}, assigned=${assigned}, skipped=${skipped}`);
                        }
                      } catch (groupErr: any) {
                        console.warn(`[PermSync] Group assign failed for ${deviceInfo.id}:`, groupErr?.message);
                      }
                    }
                  } else {
                    markZoneLocalFailure(deviceInfo.id, deviceName);
                  }
                } catch {
                  markZoneLocalFailure(deviceInfo.id, deviceName);
                }
              } else if (isElectronApp) {
                markZoneLocalFailure(deviceInfo.id, deviceName);
              } else {
                cloudFallbackIds.push(deviceInfo.id);
              }

              processed++;
              setSyncProgressData({ zoneName: zoneProgressTitle, personPhotoUrl: zonePersonPhotoUrl, total: deviceIds.length, done: processed, ok: zoneProgressOk, fail: zoneProgressFail, recentItems: [...zoneProgressItems], isComplete: false });
              const elapsed = Date.now() - deviceStart;
              if (elapsed < 500) await new Promise(r => setTimeout(r, 500 - elapsed));
            }

            // ARCHITECTURE RULE: in Electron, sync is executed LOCALLY only — devices
            // that could not be reached locally are reported as failures, never
            // delegated to the cloud. The cloud batch below is the web-browser arm.
            if (cloudFallbackIds.length > 0 && isElectronApp) {
              // Only the devices whose details never loaded remain here.
              cloudFallbackIds.forEach(id => {
                markZoneLocalFailure(id, zone.devices?.find(d => d.id === id)?.name || id);
              });
            } else if (cloudFallbackIds.length > 0) {
              console.log('[Zone Sync] Cloud API for', cloudFallbackIds.length, 'device(s)');
              try {
                const cloudResponse = await api.post(
                  `/customers/${person.customer!.id}/people/${person.id}/sync`,
                  { device_ids: cloudFallbackIds }
                );
                const cloudResult = cloudResponse.data;
                const cloudSuccessful: any[] = cloudResult.successful_devices || [];
                const cloudFailed: any[] = cloudResult.failed_devices || [];

                cloudSuccessful.forEach((d: any) => {
                  const name = zone.devices?.find(x => x.id === d.device_id)?.name || d.device_name || d.device_id;
                  zoneSyncSuccessful.push({ device_id: d.device_id, device_name: name });
                  zoneCloudSuccessCount++;
                  zoneProgressOk++;
                  zoneProgressItems.push(buildPersonSyncItem({ person: zoneSyncSubject, deviceName: name, status: 'ok' }));
                  // Audit row only: the cloud sync path records device_credential_states
                  // server-side from iot-connect's own per-device response.
                  reportDeviceSyncOutcome({
                    customerId: person.customer!.id, personId: person.id, deviceId: d.device_id,
                    success: true, source: 'cloud', recordLedger: false,
                  });
                });
                cloudFailed.forEach((d: any) => {
                  const name = zone.devices?.find(x => x.id === d.device_id)?.name || d.device_name || d.device_id;
                  const errMsg = d.error || 'Falha na nuvem';
                  zoneSyncFailed.push({ device_id: d.device_id, device_name: name, error: errMsg });
                  zoneProgressFail++;
                  zoneProgressItems.push(buildPersonSyncItem({ person: zoneSyncSubject, deviceName: name, status: 'failed', error: errMsg }));
                });

                // If API returned no arrays, use devices_synced count
                if (cloudSuccessful.length === 0 && cloudFailed.length === 0) {
                  const cloudSynced = cloudResult.devices_synced || 0;
                  cloudFallbackIds.slice(0, cloudSynced).forEach(id => {
                    const name = zone.devices?.find(d => d.id === id)?.name || id;
                    zoneSyncSuccessful.push({ device_id: id, device_name: name });
                    zoneCloudSuccessCount++;
                    zoneProgressOk++;
                    zoneProgressItems.push(buildPersonSyncItem({ person: zoneSyncSubject, deviceName: name, status: 'ok' }));
                  });
                  cloudFallbackIds.slice(cloudSynced).forEach(id => {
                    const name = zone.devices?.find(d => d.id === id)?.name || id;
                    zoneSyncFailed.push({ device_id: id, device_name: name, error: 'Falha na nuvem' });
                    zoneProgressFail++;
                    zoneProgressItems.push(buildPersonSyncItem({ person: zoneSyncSubject, deviceName: name, status: 'failed', error: 'Falha na nuvem' }));
                  });
                }
              } catch (cloudErr: any) {
                cloudFallbackIds.forEach(id => {
                  const name = zone.devices?.find(d => d.id === id)?.name || id;
                  const errMsg = cloudErr.response?.data?.error || 'Falha na nuvem';
                  zoneSyncFailed.push({ device_id: id, device_name: name, error: errMsg });
                  zoneProgressFail++;
                  zoneProgressItems.push(buildPersonSyncItem({ person: zoneSyncSubject, deviceName: name, status: 'failed', error: errMsg }));
                });
              }
            }

            // Mark progress complete
            setSyncProgressData({
              zoneName: zoneProgressTitle,
              personPhotoUrl: zonePersonPhotoUrl,
              total: deviceIds.length,
              done: deviceIds.length,
              ok: zoneProgressOk,
              fail: zoneProgressFail,
              recentItems: [...zoneProgressItems],
              isComplete: true,
            });

            // Build final status
            const totalZoneDevices = deviceIds.length;
            const zoneSuccess = zoneSyncSuccessful.length;
            const zoneFail = zoneSyncFailed.length;
            let zoneSyncResult: 'success' | 'error' | 'partial' = 'success';
            let zoneSyncMessage = '';

            const zoneSourceAnnotation = (() => {
              if (zoneLocalSuccessCount > 0 && zoneCloudSuccessCount > 0) return ` (${zoneLocalSuccessCount} local, ${zoneCloudSuccessCount} nuvem)`;
              if (zoneLocalSuccessCount > 0) return ` (${zoneLocalSuccessCount} local)`;
              if (zoneCloudSuccessCount > 0) return ` (${zoneCloudSuccessCount} nuvem)`;
              return '';
            })();

            if (zoneFail > 0 && zoneSuccess === 0) {
              zoneSyncResult = 'error';
              zoneSyncMessage = `Falha ao sincronizar ${totalZoneDevices} dispositivo(s) em "${zoneLabel}"`;
              toast.error(zoneSyncMessage, { duration: 5000 });
            } else if (zoneFail > 0) {
              zoneSyncResult = 'partial';
              zoneSyncMessage = `Sincronizado parcialmente: ${zoneSuccess} de ${totalZoneDevices} dispositivo(s) em "${zoneLabel}"${zoneSourceAnnotation}`;
              toast(zoneSyncMessage, { duration: 5000 });
            } else {
              zoneSyncResult = 'success';
              zoneSyncMessage = `${zoneSuccess} dispositivo(s) sincronizado(s) em "${zoneLabel}"${zoneSourceAnnotation}`;
              toast.success(zoneSyncMessage, { duration: 4000 });
            }

            setSyncStatus({
              isSyncing: false,
              lastSyncAt: new Date(),
              syncResult: zoneSyncResult,
              syncMessage: zoneSyncMessage,
              successfulDevices: zoneSyncSuccessful,
              failedDevices: zoneSyncFailed,
            });

          // Refresh device enrollment status
          fetchAccessZones();
        } catch (err: any) {
          console.error('Failed to sync zone:', err);
          toast.error('Erro ao sincronizar zona');
        } finally {
          setSyncingZoneId(null);
        }
      },
    });
  };

  // Revoke all access for this person from all devices (HYBRID: local → public → cloud)
  const handleRevokeAllAccess = async () => {
    if (!person.customer?.id || !person.id) return;

    // Collect ALL devices from all zones (including non-synced — backend marks them revoked)
    const seenDeviceIds = new Set<string>();
    const allDevices: { id: string; name: string; zoneId: string; zoneName: string }[] = [];
    accessZones.forEach(zone => {
      zone.devices?.forEach(device => {
        if (!seenDeviceIds.has(device.id)) {
          seenDeviceIds.add(device.id);
          allDevices.push({ id: device.id, name: device.name, zoneId: zone.id, zoneName: zone.name });
        }
      });
    });

    if (allDevices.length === 0) {
      toast.error('Nenhum dispositivo para revogar');
      return;
    }

    showConfirmModal({
      title: 'Revogar Todos os Acessos',
      message: `Tem certeza que deseja revogar TODOS os acessos desta pessoa de ${allDevices.length} dispositivo(s)?\n\nEsta ação removerá a pessoa de todos os dispositivos de controle de acesso.`,
      confirmText: 'Revogar Todos',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      icon: 'revoke',
      showDisclaimer: true,
      disclaimerType: 'revoke',
      onConfirm: async () => {
        setSyncStatus(prev => ({ ...prev, isSyncing: true, syncResult: null, syncMessage: '' }));

        const successfulDevices: { device_id: string; device_name: string }[] = [];
        const failedDevices: { device_id: string; device_name: string; error: string }[] = [];
        let processed = 0;

        const toastId = notify.loading('Revogando acesso', `0 de ${allDevices.length} dispositivos`);

        try {
          // Build device details map for HYBRID — use cached credentials first
          const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;
          const deviceDetailsMap = new Map<string, DeviceInfo>(deviceInfoCacheRef.current);
          if (isElectronApp) {
            const uncached = allDevices.filter(d => !deviceDetailsMap.has(d.id));
            if (uncached.length > 0) {
              const results = await Promise.allSettled(uncached.map(d => fetchDeviceDetails(d.id)));
              results.forEach((r, i) => {
                if (r.status === 'fulfilled' && r.value) deviceDetailsMap.set(uncached[i].id, r.value);
              });
            }
          }

          // Process devices sequentially with a minimum 500ms delay each
          for (const device of allDevices) {
            const deviceStart = Date.now();
            try {
              const deviceInfo = deviceDetailsMap.get(device.id);
              const hasDirectIP = deviceInfo && (deviceInfo.local_ip_address || deviceInfo.ip_address);

              // HYBRID: try direct local removal first (Electron only, device with IP)
              let directFailed = false;
              if (isElectronApp && hasDirectIP) {
                try {
                  const result = await DeviceIntegration.instance.execute(
                    deviceInfo!,
                    DeviceOperations.REMOVE_PERSON,
                    { person_id: person.id },
                    CommunicationMode.HYBRID
                  );
                  console.log(`[RevokeAll] ${result.success ? '' : ''} Direct (${result.source}) for ${device.name}: success=${result.success}`);
                  if (!result.success) directFailed = true;
                } catch (directErr) {
                  console.warn(`[RevokeAll] Direct attempt failed for ${device.name}:`, directErr);
                  directFailed = true;
                }
              }

              if (directFailed) {
                throw new Error('Falha na comunicação com o dispositivo');
              }

              // Mark revoked in DB — only reached if HYBRID succeeded or no direct IP
              if (isElectronApp) {
                await api.patch(`/customers/${person.customer.id}/people/${person.id}/devices/${device.id}/mark-revoked`);
              } else {
                await api.delete(`/customers/${person.customer.id}/people/${person.id}/devices/${device.id}/revoke`);
              }
              successfulDevices.push({ device_id: device.id, device_name: device.name });
            } catch (err: any) {
              failedDevices.push({
                device_id: device.id,
                device_name: device.name,
                error: err.response?.data?.error || 'Erro ao revogar',
              });
            }
            processed++;
            notify.loading('Revogando acesso', `${processed} de ${allDevices.length} dispositivos`, { id: toastId });

            // Enforce minimum 500ms per device
            const elapsed = Date.now() - deviceStart;
            if (elapsed < 500) await new Promise(r => setTimeout(r, 500 - elapsed));
          }

          toast.dismiss(toastId);

          let syncResult: 'success' | 'error' | 'partial' = 'success';
          let syncMessage = '';

          if (failedDevices.length > 0 && successfulDevices.length === 0) {
            syncResult = 'error';
            syncMessage = `Falha ao revogar ${allDevices.length} dispositivo(s)`;
            toast.error(syncMessage, { duration: 5000 });
          } else if (failedDevices.length > 0) {
            syncResult = 'partial';
            syncMessage = `Revogado parcialmente: ${successfulDevices.length} de ${allDevices.length} dispositivo(s)`;
            toast(syncMessage, { duration: 5000 });
          } else {
            syncResult = 'success';
            syncMessage = `Acesso revogado de ${successfulDevices.length} dispositivo(s)`;
            toast.success(syncMessage, { duration: 4000 });
          }

          setSyncStatus({ isSyncing: false, lastSyncAt: new Date(), syncResult, syncMessage, successfulDevices, failedDevices });
          fetchAccessZones();
        } catch (err: any) {
          toast.dismiss(toastId);
          console.error('Failed to revoke all access:', err);
          toast.error('Erro ao revogar acessos');
          setSyncStatus({ isSyncing: false, lastSyncAt: new Date(), syncResult: 'error', syncMessage: 'Erro ao revogar acessos', successfulDevices, failedDevices });
        } finally {
          // Always ensure isSyncing is reset even on unhandled exceptions
          setSyncStatus(prev => prev.isSyncing ? { ...prev, isSyncing: false } : prev);
        }
      },
    });
  };

  // Revoke access for a specific zone (removes from all devices in that zone)
  const handleRevokeZoneAccess = async (zoneId: string, zoneName: string) => {
    if (!person.customer?.id || !person.id) return;

    showConfirmModal({
      title: 'Revogar Acesso à Zona',
      message: `Tem certeza que deseja revogar o acesso à zona "${zoneName}"?\n\nEsta ação removerá a pessoa de todos os dispositivos desta zona.`,
      confirmText: 'Revogar Acesso',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      icon: 'revoke',
      showDisclaimer: true,
      disclaimerType: 'revoke',
      onConfirm: async () => {
        setSyncingZoneId(zoneId);
        const personName = `${person.first_name} ${person.last_name}`.trim();
        const toastId = toast.loading(`Revogando acesso de ${personName} na zona "${zoneName}"...`);

        try {
          const zone = accessZones.find(z => z.id === zoneId);
          const zoneDevices = zone?.devices ?? [];
          const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;

          if (isElectronApp && zoneDevices.length > 0) {
            // Electron: HYBRID direct removal from each device, then mark-revoked in DB
            let revokedCount = 0;
            for (const device of zoneDevices) {
              try {
                let deviceInfo = deviceInfoCacheRef.current.get(device.id) ?? null;
                if (!deviceInfo) {
                  deviceInfo = await fetchDeviceDetails(device.id);
                  if (deviceInfo) deviceInfoCacheRef.current.set(device.id, deviceInfo);
                }
                let zoneDirectFailed = false;
                if (deviceInfo && (deviceInfo.local_ip_address || deviceInfo.ip_address)) {
                  const opResult = await DeviceIntegration.instance.execute(
                    deviceInfo,
                    DeviceOperations.REMOVE_PERSON,
                    { person_id: person.id },
                    CommunicationMode.HYBRID
                  );
                  console.log(`[RevokeZone] ${opResult.success ? '' : ''} Device ${device.id} (${opResult.source}): success=${opResult.success}`);
                  if (!opResult.success) zoneDirectFailed = true;
                } else {
                  console.log(`[RevokeZone] No IP for device ${device.id}, skipping HYBRID — will still mark-revoked in DB`);
                }
                if (!zoneDirectFailed) {
                  await api.patch(`/customers/${person.customer.id}/people/${person.id}/devices/${device.id}/mark-revoked`);
                  revokedCount++;
                }
              } catch (deviceErr) {
                console.warn(`[RevokeZone] Failed for device ${device.id}:`, deviceErr);
              }
            }
            toast.success(`Acesso de ${personName} revogado de ${revokedCount} dispositivo(s) na zona "${zoneName}"`, { id: toastId });
          } else {
            // Non-Electron fallback: backend handles device communication
            const response = await api.delete(`/customers/${person.customer.id}/people/${person.id}/zones/${zoneId}/revoke`);
            const devicesRevoked = response.data?.devices_revoked || 0;
            toast.success(`Acesso de ${personName} revogado de ${devicesRevoked} dispositivo(s) na zona "${zoneName}"`, { id: toastId });
          }

          fetchAccessZones();
        } catch (err: any) {
          console.error('Failed to revoke zone access:', err);
          toast.error(err.response?.data?.error || 'Erro ao revogar acesso à zona', { id: toastId });
        } finally {
          setSyncingZoneId(null);
        }
      },
    });
  };

  // Revoke access for a specific device (HYBRID: local → cloud).
  // `zoneId` is optional because Dispositivos de Acesso can revoke a credential that belongs
  // to NO current zone — the stranded case this whole section exists to surface.
  const handleRevokeDeviceAccess = async (deviceId: string, deviceName: string, zoneId?: string) => {
    if (!person.customer?.id || !person.id) return;

    const personName = `${person.first_name} ${person.last_name}`.trim();
    const toastId = toast.loading(`Revogando acesso de ${personName} em "${deviceName}"...`);

    try {
      const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;

      if (isElectronApp) {
        // Electron: HYBRID direct removal, then mark-revoked in DB only if it succeeded
        let deviceDirectFailed = false;
        try {
          let deviceInfo = deviceInfoCacheRef.current.get(deviceId) ?? null;
          if (!deviceInfo) {
            console.log('[RevokeDevice] Cache miss — fetching device details for', deviceId);
            deviceInfo = await fetchDeviceDetails(deviceId);
            if (deviceInfo) deviceInfoCacheRef.current.set(deviceId, deviceInfo);
          }
          console.log('[RevokeDevice] deviceInfo:', deviceInfo
            ? `vendor=${deviceInfo.vendor} ip=${deviceInfo.ip_address || deviceInfo.local_ip_address || 'NONE'}`
            : 'null (HYBRID will be skipped)');
          if (deviceInfo && (deviceInfo.local_ip_address || deviceInfo.ip_address)) {
            const result = await DeviceIntegration.instance.execute(
              deviceInfo,
              DeviceOperations.REMOVE_PERSON,
              { person_id: person.id },
              CommunicationMode.HYBRID
            );
            console.log(`[RevokeDevice] ${result.success ? '' : ''} Direct (${result.source}): success=${result.success}`);
            if (!result.success) deviceDirectFailed = true;
          } else {
            console.warn('[RevokeDevice] Skipping HYBRID: no device info or no IP address');
          }
        } catch (directErr) {
          console.warn(`[RevokeDevice] Direct attempt failed:`, directErr);
          deviceDirectFailed = true;
        }
        if (deviceDirectFailed) {
          throw new Error('Falha na comunicação com o dispositivo. Tente novamente.');
        }
        // Mark revoked in DB only if HYBRID succeeded (or no IP to attempt)
        await api.patch(`/customers/${person.customer.id}/people/${person.id}/devices/${deviceId}/mark-revoked`);
      } else {
        // Non-Electron fallback: backend handles device communication
        await api.delete(`/customers/${person.customer.id}/people/${person.id}/devices/${deviceId}/revoke`);
      }

      toast.success(`Acesso de ${personName} revogado em "${deviceName}"`, { id: toastId });

      // Every view answering "is this person on that reader" re-reads on this signal —
      // without it, Dispositivos de Acesso would keep showing the credential it just removed.
      listRefreshEvents.emit('device-credentials');

      // Update local state immediately
      setAccessZones(prevZones =>
        prevZones.map(zone =>
          zone.id === zoneId
            ? {
                ...zone,
                devices: zone.devices?.map(device =>
                  device.id === deviceId
                    ? { ...device, is_synced: false, removal_reason: 'revoked', removed_at: new Date().toISOString() }
                    : device
                ),
              }
            : zone
        )
      );
      setDeviceEnrollment(prev => ({
        ...prev,
        [deviceId]: { is_enrolled: false, removal_reason: 'revoked', removed_at: new Date().toISOString() },
      }));
    } catch (err: any) {
      console.error('Failed to revoke device access:', err);
      toast.error(err.response?.data?.error || 'Erro ao revogar acesso ao dispositivo', { id: toastId });
    }
  };

  /** Dispositivos de Acesso row action: confirm, then revoke that one device. */
  const confirmRevokeDevice = (deviceId: string, deviceName: string) => {
    const personName = `${person.first_name} ${person.last_name}`.trim();
    showConfirmModal({
      title: 'Revogar Dispositivo',
      message: `Remover ${personName} de "${deviceName}"?\n\nA credencial sai do dispositivo. As zonas e perfis da pessoa não mudam.`,
      confirmText: 'Revogar',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      icon: 'revoke',
      showDisclaimer: true,
      disclaimerType: 'revoke',
      onConfirm: async () => {
        await handleRevokeDeviceAccess(deviceId, deviceName);
      },
    });
  };

  // Format last sync date
  const formatLastSync = (date: Date | null) => {
    if (!date) return null;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins < 60) return `${diffMins}min atras`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h atras`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const personRoleTypes = person.roles?.map(r => r.role_type) || [];
  const availableToAdd = availableRoleTypes.filter(rt => !personRoleTypes.includes(rt.value));

  // Why the zone table below may be describing access that is not in force — see accessInEffect.ts.
  const accessNotInEffect = accessNotInEffectReason(person.roles);

  // The person's relation to their unit (condomínio) or departamento (empresa), and whether the
  // administração has already opened an exception for them. Both live in role metadata, which is
  // why they are read off the roles rather than off the person.
  const assignmentRole = (person.roles ?? []).find(
    (role) => role.is_active && (role.role_type === 'resident' || role.role_type === 'staff'),
  );
  const assignmentRelation = (assignmentRole?.role_metadata?.relation_type as string) ?? '';
  const hasVisitorAuthorizerFlag = (person.roles ?? []).some(
    (role) => role.is_active && role.role_metadata?.can_authorize_visitors === true,
  );
  const customerIsEmpresa = person.customer?.type === 'empresa';
  const visitorAuthorizerBlob = customerIsEmpresa
    ? person.customer?.empresa_data
    : person.customer?.condo_data;

  return (
    <div className="space-y-6">
      {person.customer?.id && (
        <VisitorAuthorizerPersonToggle
          customerId={person.customer.id}
          personId={person.id}
          relation={assignmentRelation}
          isEmpresa={customerIsEmpresa}
          controlEnabled={
            visitorAuthorizerBlob?.visitor_authorization_control_enabled ?? false
          }
          allowedRelations={
            visitorAuthorizerBlob?.visitor_authorizer_relations ?? []
          }
          hasFlag={hasVisitorAuthorizerFlag}
          readOnly={readOnly}
          darkMode={darkMode}
          onRefresh={onRefresh}
        />
      )}
      {/* Roles — header + list in one bordered block */}
      <div className={cn("rounded-lg border overflow-hidden", darkMode ? "border-gray-700" : "border-gray-200")}>

        {/* Header row */}
        <div className={cn(
          "flex items-center justify-between px-3 py-2 border-b",
          darkMode ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"
        )}>
          <div className="flex items-center gap-1.5 relative">
            <span className={cn("text-[10px] font-semibold uppercase tracking-widest", darkMode ? "text-gray-500" : "text-gray-400")}>
              Funções Atuais
            </span>
            <button
              ref={roleHelpAnchorRef}
              type="button"
              aria-label="Como funcionam as funções"
              onClick={toggleRoleHelp}
              className={cn(
                "rounded-full transition-colors",
                darkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
              )}
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>

            {/* Portalled: the roles card is overflow-hidden, which clipped this to ~1.5 lines
                when it was an absolutely-positioned child. Fixed coords come from the anchor. */}
            {showRoleHelp && createPortal(
              <>
                {/* Click-away. Pointer-events only; no dimming, so the card stays readable. */}
                <div className="fixed inset-0 z-[998]" onClick={() => setShowRoleHelp(false)} />
                <div
                  role="tooltip"
                  style={{ top: roleHelpPos.top, left: roleHelpPos.left }}
                  className={cn(
                    "fixed z-[999] w-[22rem] max-w-[calc(100vw-2rem)] p-3 rounded-lg shadow-xl border text-xs leading-relaxed",
                    darkMode ? "bg-gray-900 border-gray-700 text-gray-300" : "bg-white border-gray-200 text-gray-600"
                  )}
                >
                  <p className={cn("font-semibold mb-1.5", darkMode ? "text-gray-100" : "text-gray-900")}>
                    Como o acesso é concedido
                  </p>
                  <p className="mb-2">
                    <strong>Função + unidade</strong> → a unidade tem um <strong>perfil de acesso</strong> →
                    o perfil libera <strong>zonas</strong> → cada zona tem os <strong>dispositivos</strong>.
                    A pessoa só abre uma porta quando essa corrente está completa.
                  </p>
                  <p className="mb-2">
                    Por isso um <strong>Morador sem unidade não abre nada</strong>, mesmo aparecendo como
                    Ativa e Autorizado — não há unidade de onde herdar o perfil. A unidade é pedida junto
                    com a função, e pode ser corrigida no botão de unidade da própria função.
                  </p>
                  <p>
                    <strong>Autorizar</strong> libera a função; <strong>Sincronizar</strong> grava a pessoa
                    nos dispositivos das zonas. As duas coisas são necessárias.
                  </p>
                </div>
              </>,
              document.body,
            )}
          </div>
          {!readOnly && !addingRole && availableToAdd.length > 0 && (
            <button
              onClick={() => setAddingRole(true)}
              className={cn(
                "flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-colors",
                darkMode ? "text-blue-400 hover:bg-blue-900/30" : "text-blue-600 hover:bg-blue-50"
              )}
            >
              <Plus className="w-3.5 h-3.5" />
              Adicionar
            </button>
          )}
        </div>

        {/* Inline add form */}
        {!readOnly && addingRole && (
          <div className={cn(
            "px-3 py-2.5 border-b space-y-2",
            darkMode ? "bg-gray-800/40 border-gray-700" : "bg-blue-50/60 border-gray-200"
          )}>
            <div className="flex items-center gap-2">
              <select
                aria-label="role-type-selector"
                value={selectedRoleType}
                onChange={(e) => {
                  setSelectedRoleType(e.target.value);
                  // Carrying a unit across a role change would silently attach it to a role the
                  // operator never picked it for.
                  setSelectedUnit(null);
                  setSelectedRelationType('proprietario');
                }}
                className={cn(
                  "flex-1 px-2 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                  darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                )}
              >
                <option value="">Selecione uma função...</option>
                {availableToAdd.map((rt) => (
                  <option key={rt.value} value={rt.value}>{rt.label}</option>
                ))}
              </select>
              <button
                onClick={handleAddRole}
                disabled={addRoleBlocked}
                className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Confirmar
              </button>
              <button
                onClick={resetAddRoleForm}
                className={cn(
                  "px-2 py-1.5 text-xs rounded-md transition-colors",
                  darkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100"
                )}
              >
                Cancelar
              </button>
            </div>

            {/* Unit + relation, asked here so the role is never created without the unit that
                gives it access. Previously this meant a second trip to Visão Geral. */}
            {needsUnit && (
              <div className="space-y-2">
                {/* UnitSelector renders its own labelled 2-column grid, so it gets a full row.
                    Sitting it beside another control collided with its labels and overlapped
                    the hint below. */}
                <UnitSelector
                  customerId={person.customer?.id || ''}
                  value={selectedUnit}
                  onChange={setSelectedUnit}
                  darkMode={darkMode}
                  condoType={(person.customer as any)?.condoType || (person.customer as any)?.condo_data?.condo_type}
                />

                <div>
                  <label className={cn(
                    "block text-[10px] font-semibold tracking-widest uppercase mb-1",
                    darkMode ? "text-gray-400" : "text-gray-500"
                  )}>
                    Relação
                  </label>
                  <select
                    aria-label="relation-type-selector"
                    value={selectedRelationType}
                    onChange={(e) => setSelectedRelationType(e.target.value)}
                    className={cn(
                      "w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                      darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                    )}
                  >
                    {ROLE_RELATION_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                {!selectedUnit && (
                  <p className={cn(
                    "flex items-start gap-1.5 text-[11px] leading-snug",
                    darkMode ? "text-amber-400" : "text-amber-700"
                  )}>
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                    <span>
                      Selecione a unidade. Uma função sem unidade não concede acesso algum — ela
                      aparece como Ativa, mas não há unidade de onde herdar o perfil de acesso.
                    </span>
                  </p>
                )}
              </div>
            )}

            {/* Department, for staff roles. Asked here for the same reason the unit is: without
                it the backend files the person under "Sem Departamento", which has no profile. */}
            {needsDepartment && (
              <div className="space-y-2">
                <div>
                  <label className={cn(
                    "block text-[10px] font-semibold tracking-widest uppercase mb-1",
                    darkMode ? "text-gray-400" : "text-gray-500"
                  )}>
                    Departamento
                  </label>
                  {/* Multi-select: one person can belong to several departments, which the
                      backend stores as one role row each. */}
                  <div
                    aria-label="department-selector"
                    className={cn(
                      "max-h-36 overflow-y-auto rounded-lg border divide-y",
                      darkMode ? "bg-gray-800 border-gray-600 divide-gray-700" : "bg-white border-gray-300 divide-gray-100"
                    )}
                  >
                    {selectableDepartments.length === 0 ? (
                      <p className={cn("px-2 py-2 text-xs", darkMode ? "text-gray-500" : "text-gray-400")}>
                        Nenhum departamento cadastrado
                      </p>
                    ) : selectableDepartments.map(d => (
                      <label
                        key={d.id}
                        className={cn(
                          "flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer",
                          darkMode ? "text-gray-100 hover:bg-gray-700/50" : "text-gray-900 hover:bg-gray-50"
                        )}
                      >
                        <input
                          type="checkbox"
                          aria-label={`department-option-${d.id}`}
                          checked={selectedDepartmentIds.includes(d.id)}
                          onChange={() => setSelectedDepartmentIds(prev => toggleIn(prev, d.id))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {d.name}
                      </label>
                    ))}
                  </div>
                </div>

                {selectedDepartmentIds.length === 0 && (
                  <p className={cn(
                    "flex items-start gap-1.5 text-[11px] leading-snug",
                    darkMode ? "text-amber-400" : "text-amber-700"
                  )}>
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                    <span>
                      {selectableDepartments.length === 0
                        ? 'Nenhum departamento cadastrado. Crie um em Departamentos antes de atribuir esta função — sem departamento a pessoa fica em "Sem Departamento" e não recebe acesso algum.'
                        : 'Selecione o departamento. Sem ele a pessoa entra em "Sem Departamento", que não tem perfil de acesso — a função aparece como Ativa e não concede acesso algum.'}
                    </span>
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Role rows */}
        {person.roles && person.roles.length > 0 ? (() => {
          const roleGroups: { [key: string]: typeof person.roles } = {};
          person.roles.forEach(role => {
            if (!roleGroups[role.role_type]) roleGroups[role.role_type] = [];
            roleGroups[role.role_type].push(role);
          });

          return Object.entries(roleGroups).map(([roleType, roles], gi) => {
            const isStaff = roleType === 'staff';
            const allActive = roles.every(r => r.is_active);
            const allAuthorized = roles.every(r => r.is_authorized);
            const someActive = roles.some(r => r.is_active);

            if (isStaff) {
              return (
                <div key={roleType} className={cn(gi > 0 && (darkMode ? "border-t border-gray-800" : "border-t border-gray-100"))}>
                  {/* Staff group header */}
                  <div className={cn(
                    "flex items-center justify-between px-3 py-2.5",
                    darkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50/60"
                  )}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={cn("text-sm font-semibold", darkMode ? "text-gray-100" : "text-gray-900")}>
                        {roleTypeLabel(roleType)}
                      </span>
                      <span className={cn(
                        "text-xs font-medium",
                        allActive ? (darkMode ? "text-green-400" : "text-green-600")
                          : someActive ? (darkMode ? "text-amber-400" : "text-amber-600")
                          : (darkMode ? "text-gray-500" : "text-gray-400")
                      )}>
                        {allActive ? '● Ativas' : someActive ? '● Parcial' : '● Inativas'}
                      </span>
                      {allAuthorized && (
                        <span className={cn("text-xs font-medium", darkMode ? "text-green-400" : "text-green-600")}>Autorizado</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {!readOnly && (
                        <button
                          aria-label="Editar departamentos"
                          onClick={() => (editingDeptRoleId === roles[0]?.id ? setEditingDeptRoleId(null) : startEditingDepartments(roles))}
                          className={cn(
                            "px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap",
                            darkMode ? "text-blue-400 hover:bg-blue-900/30" : "text-blue-600 hover:bg-blue-50"
                          )}
                        >
                          Departamentos
                        </button>
                      )}
                      {!allAuthorized && (
                        <button
                          onClick={async () => { for (const r of roles.filter(r => !r.is_authorized)) await handleAuthorizeRole(r.id); }}
                          className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded transition-colors whitespace-nowrap"
                        >
                          Autorizar
                        </button>
                      )}
                      {someActive && (
                        <button
                          onClick={async () => { for (const r of roles.filter(r => r.is_active)) await handleToggleRoleActive(r.id, r.is_active); }}
                          className={cn("px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap", darkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100")}
                        >
                          Desativar
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Department sub-rows */}
                  {roles.map((role, ri) => (
                    <div
                      key={role.id}
                      className={cn(
                        "px-3 py-2",
                        darkMode ? "border-t border-gray-800/70 bg-gray-900/30" : "border-t border-gray-100 bg-gray-50/40"
                      )}
                    >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <span className={cn("w-1 h-1 rounded-full flex-shrink-0", darkMode ? "bg-gray-600" : "bg-gray-300")} />
                        <span className={cn("text-xs truncate", darkMode ? "text-gray-300" : "text-gray-700")}>
                          {role.role_metadata?.department_name || 'Departamento'}
                        </span>

                        {/* "Sem Departamento" and a missing department are the same thing for
                            access: no profile to inherit, so the role grants nothing. */}
                        {(!role.role_metadata?.department_id
                          || isFallbackDepartment(role.role_metadata?.department_name)) && (
                          <span className={cn(
                            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
                            darkMode ? "bg-amber-900/40 text-amber-400" : "bg-amber-100 text-amber-700"
                          )}>
                            <AlertCircle className="w-3 h-3" />
                            Sem departamento — não concede acesso
                          </span>
                        )}
                        <span className={cn("text-[10px] font-medium", (role.is_active || role.is_authorized === false) ? (darkMode ? "text-green-400" : "text-green-600") : (darkMode ? "text-gray-500" : "text-gray-400"))}>
                          {(role.is_active || role.is_authorized === false) ? '● Ativa' : '● Inativa'}
                        </span>
                        {role.is_authorized !== undefined && (
                          <span className={cn("text-[10px] font-medium", role.is_authorized ? (darkMode ? "text-green-400" : "text-green-600") : (darkMode ? "text-amber-400" : "text-amber-600"))}>
                            {role.is_authorized ? 'Autorizado' : 'Pendente'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {role.is_authorized === false && (
                          <button onClick={() => handleAuthorizeRole(role.id)} className="px-2 py-0.5 text-[11px] font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded transition-colors">
                            Autorizar
                          </button>
                        )}
                        {role.is_authorized !== false && (
                          <button
                            onClick={() => handleToggleRoleActive(role.id, role.is_active)}
                            className={cn("px-2 py-0.5 text-[11px] font-medium rounded transition-colors", darkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100")}
                          >
                            {role.is_active ? 'Desativar' : 'Ativar'}
                          </button>
                        )}
                      </div>
                    </div>

                    </div>
                  ))}

                  {/* Departments are edited for the whole staff group, not per sub-row: the
                      department_ids PATCH replaces the entire set in one call, creating a role
                      row per department and deactivating the ones left out. */}
                  {editingDeptRoleId === roles[0]?.id && (
                    <div className={cn(
                      "m-2 p-2.5 rounded-md border",
                      darkMode ? "bg-gray-800/50 border-gray-700" : "bg-blue-50/60 border-blue-100"
                    )}>
                      <div
                        className={cn(
                          "max-h-36 overflow-y-auto rounded-lg border divide-y",
                          darkMode ? "bg-gray-800 border-gray-600 divide-gray-700" : "bg-white border-gray-300 divide-gray-100"
                        )}
                      >
                        {selectableDepartments.length === 0 ? (
                          <p className={cn("px-2 py-2 text-xs", darkMode ? "text-gray-500" : "text-gray-400")}>
                            Nenhum departamento cadastrado
                          </p>
                        ) : selectableDepartments.map(d => (
                          <label
                            key={d.id}
                            className={cn(
                              "flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer",
                              darkMode ? "text-gray-100 hover:bg-gray-700/50" : "text-gray-900 hover:bg-gray-50"
                            )}
                          >
                            <input
                              type="checkbox"
                              aria-label={`edit-department-option-${d.id}`}
                              checked={editingDepartmentIds.includes(d.id)}
                              onChange={() => setEditingDepartmentIds(prev => toggleIn(prev, d.id))}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            {d.name}
                          </label>
                        ))}
                      </div>

                      <p className={cn("flex items-start gap-1.5 text-[11px] leading-snug mt-2", darkMode ? "text-amber-400" : "text-amber-700")}>
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                        <span>
                          Esta lista substitui os departamentos atuais — os que ficarem
                          desmarcados serão removidos desta pessoa.
                        </span>
                      </p>

                      <div className="flex items-center justify-end gap-2 mt-2">
                        <button
                          onClick={() => { setEditingDeptRoleId(null); setEditingDepartmentIds([]); }}
                          className={cn(
                            "px-2 py-1.5 text-xs rounded-md transition-colors",
                            darkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100"
                          )}
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={() => handleSaveRoleDepartments(roles[0].id)}
                          disabled={savingDepartment || editingDepartmentIds.length === 0}
                          title={editingDepartmentIds.length === 0 ? 'Selecione ao menos um departamento' : undefined}
                          className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-40 whitespace-nowrap"
                        >
                          {savingDepartment ? 'Salvando...' : 'Salvar departamentos'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Regular roles
            return roles.map((role, ri) => {
              const roleUnits = readRoleUnits(role.role_metadata);
              const roleNeedsUnit = isUnitBasedRole(role.role_type);
              const isEditingUnits = editingUnitsRoleId === role.id;

              return (
              <div
                key={role.id}
                className={cn(
                  "px-3 py-2.5",
                  (gi > 0 || ri > 0) && (darkMode ? "border-t border-gray-800" : "border-t border-gray-100"),
                  darkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50/60"
                )}
              >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-wrap">
                  <span className={cn("text-sm font-semibold", darkMode ? "text-gray-100" : "text-gray-900")}>
                    {roleTypeLabel(role.role_type)}
                  </span>
                  {role.is_active !== undefined && (
                    <span className={cn("text-xs font-medium", role.is_active ? (darkMode ? "text-green-400" : "text-green-600") : (darkMode ? "text-gray-500" : "text-gray-400"))}>
                      {role.is_active ? '● Ativa' : '● Inativa'}
                    </span>
                  )}
                  {role.is_authorized !== undefined && (
                    <span className={cn("text-xs font-medium", role.is_authorized ? (darkMode ? "text-green-400" : "text-green-600") : (darkMode ? "text-amber-400" : "text-amber-600"))}>
                      {role.is_authorized ? 'Autorizado' : 'Pendente'}
                    </span>
                  )}
                  {role.valid_until && (
                    <span className={cn("text-[10px]", darkMode ? "text-gray-500" : "text-gray-400")}>
                      até {new Date(role.valid_until).toLocaleDateString('pt-BR')}
                    </span>
                  )}

                  {/* The unit is what turns this role into actual access, so it belongs on the
                      row next to Ativa/Autorizado rather than one screen away. */}
                  {roleNeedsUnit && roleUnits.length > 0 && roleUnits.map(u => (
                    <span
                      key={u.unit_id}
                      className={cn(
                        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
                        darkMode ? "bg-gray-700/60 text-gray-300" : "bg-gray-100 text-gray-600"
                      )}
                    >
                      <Home className="w-3 h-3" />
                      {describeRoleUnit(u)}
                    </span>
                  ))}

                  {roleNeedsUnit && roleUnits.length === 0 && (
                    <span className={cn(
                      "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
                      darkMode ? "bg-amber-900/40 text-amber-400" : "bg-amber-100 text-amber-700"
                    )}>
                      <AlertCircle className="w-3 h-3" />
                      Sem unidade — não concede acesso
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {!readOnly && roleNeedsUnit && (
                    <button
                      aria-label="Editar unidades da função"
                      onClick={() => (isEditingUnits ? setEditingUnitsRoleId(null) : startEditingUnits(role))}
                      className={cn(
                        "px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap",
                        darkMode ? "text-blue-400 hover:bg-blue-900/30" : "text-blue-600 hover:bg-blue-50"
                      )}
                    >
                      {roleUnits.length === 0 ? 'Definir unidade' : 'Unidades'}
                    </button>
                  )}
                  {!role.is_authorized && (
                    <button
                      onClick={() => handleAuthorizeRole(role.id)}
                      className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded transition-colors whitespace-nowrap"
                    >
                      Autorizar
                    </button>
                  )}
                  <button
                    onClick={() => handleToggleRoleActive(role.id, role.is_active)}
                    className={cn(
                      "px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap",
                      role.is_active
                        ? (darkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100")
                        : (darkMode ? "text-blue-400 hover:bg-blue-900/30" : "text-blue-600 hover:bg-blue-50")
                    )}
                  >
                    {role.is_active ? 'Desativar' : 'Ativar'}
                  </button>
                </div>
              </div>

              {/* Inline unit editor — the same component Visão Geral uses, writing through the
                  same PATCH, so a unit set here is identical to one set there. */}
              {isEditingUnits && (
                <div className={cn(
                  "mt-2 p-2.5 rounded-md border",
                  darkMode ? "bg-gray-800/50 border-gray-700" : "bg-blue-50/60 border-blue-100"
                )}>
                  <UnitAssignmentManager
                    value={editingUnitAssignments}
                    customerId={person.customer?.id || ''}
                    onChange={setEditingUnitAssignments}
                    darkMode={darkMode}
                    condoType={(person.customer as any)?.condoType || (person.customer as any)?.condo_data?.condo_type}
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                      onClick={() => { setEditingUnitsRoleId(null); setEditingUnitAssignments([]); }}
                      className={cn(
                        "px-2 py-1.5 text-xs rounded-md transition-colors",
                        darkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100"
                      )}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => handleSaveRoleUnits(role.id)}
                      disabled={savingUnits}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-40 whitespace-nowrap"
                    >
                      {savingUnits ? 'Salvando...' : 'Salvar unidades'}
                    </button>
                  </div>
                </div>
              )}
              </div>
              );
            });
          });
        })() : (
          <p className={cn("text-xs text-center py-5", darkMode ? "text-gray-500" : "text-gray-400")}>
            Nenhuma função atribuída
          </p>
        )}
      </div>

      {/* Direct Person Assignments — super admin only, always expanded */}
      {!readOnly && isSuperAdmin && (
        <div className="mb-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <span className={cn("text-xs font-semibold uppercase tracking-widest", darkMode ? "text-gray-400" : "text-gray-500")}>
                Atribuições Diretas de Perfil
              </span>
              {directAssignments.length > 0 && (
                <span className={cn(
                  "px-1.5 py-0.5 rounded text-[10px] font-bold",
                  darkMode ? "bg-yellow-900/40 text-yellow-400" : "bg-yellow-100 text-yellow-700"
                )}>
                  {directAssignments.length}
                </span>
              )}
            </div>

            {/* Add a profile directly to this person — same "exception" mechanism as
                Perfis de Acesso > Atribuir Perfil > Exceções, scoped from this person's view */}
            <button
              onClick={() => { setShowAddProfilePicker(true); fetchAvailableProfiles(); }}
              className={cn(
                "flex-shrink-0 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors",
                darkMode
                  ? "border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-750 hover:border-gray-600"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
              )}
            >
              Atribuir Perfil
            </button>
          </div>

          <div className="space-y-2">
            {loadingDirectAssignments ? (
              <p className={cn("text-xs text-center py-3", darkMode ? "text-gray-500" : "text-gray-400")}>
                Carregando...
              </p>
            ) : directAssignments.length > 0 ? (
              <>
                {directAssignments.map((assignment) => {
                  const hasZoneRestrictions = assignment.zones && assignment.zones.length > 0;
                  const assignmentTypeLabel = assignment.assignment_type === 'person_type'
                    ? `Tipo de pessoa (${assignment.person_type})`
                    : 'Atribuição direta';

                  return (
                    <div
                      key={assignment.id}
                      className={cn(
                        "flex items-start gap-3 px-3.5 py-3 rounded-lg border",
                        darkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
                      )}
                    >
                      <div className={cn(
                        "flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center",
                        darkMode ? "bg-blue-900/30" : "bg-blue-50"
                      )}>
                        <ShieldCheck className={cn("w-4.5 h-4.5", darkMode ? "text-blue-400" : "text-blue-600")} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm font-semibold truncate", darkMode ? "text-gray-100" : "text-gray-900")}>
                          {assignment.access_profile?.name || 'Perfil desconhecido'}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          <span className={cn(
                            "px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide",
                            darkMode ? "bg-gray-800 text-gray-400" : "bg-gray-100 text-gray-500"
                          )}>
                            {assignmentTypeLabel}
                          </span>
                          {hasZoneRestrictions ? (
                            <span className={cn(
                              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold",
                              darkMode ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-600"
                            )}>
                              <MapPin className="w-2.5 h-2.5" />
                              {assignment.zones.map((z: any) => z.zone?.name || 'Zona').join(', ')}
                            </span>
                          ) : (
                            <span className={cn(
                              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide",
                              darkMode ? "bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30" : "bg-amber-100 text-amber-700 ring-1 ring-amber-300"
                            )}>
                              <CheckCircle className="w-2.5 h-2.5" />
                              Todas as zonas do perfil
                            </span>
                          )}
                        </div>
                        {assignment.notes && (
                          <p className={cn("text-xs mt-1.5 italic", darkMode ? "text-gray-500" : "text-gray-400")}>
                            {assignment.notes}
                          </p>
                        )}
                      </div>
                      {assignment.assignment_type === 'direct' && (
                        <button
                          onClick={() => handleRemoveDirectAssignment(assignment.id, assignment.access_profile?.name || 'Perfil')}
                          className={cn(
                            "flex-shrink-0 p-1.5 rounded transition-colors",
                            darkMode
                              ? "text-gray-600 hover:text-red-400 hover:bg-red-900/20"
                              : "text-gray-300 hover:text-red-500 hover:bg-red-50"
                          )}
                          title="Remover atribuição direta"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <p className={cn("text-xs text-center py-3", darkMode ? "text-gray-500" : "text-gray-400")}>
                Nenhuma atribuição direta
              </p>
            )}
          </div>
        </div>
      )}

      {/* Person ID — moved here from the modal header */}
      <div>
        <label className={cn(
          "block text-xs font-medium mb-1",
          darkMode ? "text-gray-400" : "text-gray-500"
        )}>
          ID da Pessoa
        </label>
        <div className="flex items-center gap-2">
          <code className={cn(
            "flex-1 px-2.5 py-1.5 rounded font-mono text-xs break-all",
            darkMode ? "bg-gray-900 text-gray-300" : "bg-gray-100 text-gray-700"
          )}>
            {person.id}
          </code>
          <button
            onClick={() => {
              navigator.clipboard.writeText(person.id);
              toast.success('ID copiado!');
            }}
            className={cn(
              "flex-shrink-0 p-1.5 rounded transition-colors",
              darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-200 text-gray-500"
            )}
            title="Copiar ID"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Device IDs — collapsible, moved here from Visão Geral since it's permissions/device
          related, not part of the person's editable identity info */}
      <div>
        <button
          onClick={() => setShowDeviceIds(v => !v)}
          className={cn(
            "flex items-center gap-1.5 text-xs font-medium transition-colors",
            darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"
          )}
        >
          {showDeviceIds ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {showDeviceIds ? 'Ocultar IDs de dispositivo' : 'Ver IDs de dispositivo'}
        </button>

        {showDeviceIds && (
          <div className={cn(
            "mt-3 rounded-lg border p-3 space-y-3",
            darkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
          )}>
            {/* Hikvision ID */}
            <div>
              <label className={cn(
                "block text-xs font-medium mb-1",
                darkMode ? "text-gray-400" : "text-gray-500"
              )}>
                ID Hikvision (MD5)
              </label>
              <div className="flex items-center gap-2">
                <code className={cn(
                  "flex-1 px-2.5 py-1.5 rounded font-mono text-xs",
                  darkMode ? "bg-gray-900 text-blue-400" : "bg-blue-50 text-blue-900"
                )}>
                  {md5(person.id)}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(md5(person.id));
                    toast.success('ID Hikvision copiado!');
                  }}
                  className={cn(
                    "p-1.5 rounded transition-colors",
                    darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-200 text-gray-500"
                  )}
                  title="Copiar ID Hikvision"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className={cn("mt-0.5 text-xs", darkMode ? "text-gray-600" : "text-gray-400")}>
                Hash MD5 do UUID — registrado nos dispositivos Hikvision
              </p>
            </div>

            {/* ControlID código */}
            <div>
              <label className={cn(
                "block text-xs font-medium mb-1",
                darkMode ? "text-gray-400" : "text-gray-500"
              )}>
                ID ControlID (código)
              </label>
              <div className="flex items-center gap-2">
                <code className={cn(
                  "flex-1 px-2.5 py-1.5 rounded font-mono text-xs",
                  darkMode ? "bg-gray-900 text-blue-400" : "bg-blue-50 text-blue-900"
                )}>
                  {uuidToDeterministicControlIDId(person.id)}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(String(uuidToDeterministicControlIDId(person.id)));
                    toast.success('ID ControlID copiado!');
                  }}
                  className={cn(
                    "p-1.5 rounded transition-colors",
                    darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-200 text-gray-500"
                  )}
                  title="Copiar ID ControlID"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className={cn("mt-0.5 text-xs", darkMode ? "text-gray-600" : "text-gray-400")}>
                Código numérico determinístico — mesmo valor em todos os dispositivos ControlID
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Access Zones */}
      <div>
        {/* Header with Sync All button */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <h4 className={cn(
              "text-sm font-medium",
              darkMode ? "text-gray-300" : "text-gray-700"
            )}>
              Zonas de Acesso
            </h4>
            {/* Standing background, not a finding: it never changes and applies to every
                person, so it hangs off the heading it explains instead of occupying a box
                between the two tables an operator actually reads. */}
            <span
              aria-label="Sobre as zonas de acesso"
              title={accessZonesNote(person.customer?.type === 'empresa')}
              className={cn('cursor-help', darkMode ? 'text-gray-500' : 'text-gray-400')}
            >
              <Info className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="flex items-center gap-3">
            {/* Last sync indicator */}
            {syncStatus.lastSyncAt && (
              <div className={cn(
                "flex items-center gap-1.5 text-xs",
                darkMode ? "text-gray-400" : "text-gray-500"
              )}>
                {syncStatus.syncResult === 'success' && (
                  <Check className="w-3.5 h-3.5 text-green-500" />
                )}
                {syncStatus.syncResult === 'error' && (
                  <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                )}
                {syncStatus.syncResult === 'partial' && (
                  <AlertCircle className="w-3.5 h-3.5 text-yellow-500" />
                )}
                <Clock className="w-3.5 h-3.5" />
                <span>{formatLastSync(syncStatus.lastSyncAt)}</span>
              </div>
            )}
            {/* Sync All and Revoke All buttons */}
            {!readOnly && accessZones.length > 0 && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleSyncAllZones}
                  disabled={syncStatus.isSyncing}
                  className={`
                    flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium
                    transition-all duration-200 ease-in-out
                    ${syncStatus.isSyncing
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
                    }
                  `}
                >
                  <RefreshCw className={`w-3 h-3 ${syncStatus.isSyncing ? 'animate-spin' : ''}`} />
                  {syncStatus.isSyncing ? 'Sincronizando...' : 'Sincronizar'}
                </button>
                <button
                  onClick={handleRevokeAllAccess}
                  disabled={syncStatus.isSyncing}
                  className={`
                    flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium
                    transition-all duration-200 ease-in-out
                    ${syncStatus.isSyncing
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700'
                    }
                  `}
                  title="Revogar todos os acessos de todos os dispositivos"
                >
                  <ShieldOff className="w-3 h-3" />
                  Revogar
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {/* The table below lists what this person's PROFILES cover; the backend also requires a
              released entry window before any of it reaches a device. When the two disagree the
              screen says so, instead of showing a pré-cadastro five zones of a condomínio she has
              never been let into. See accessInEffect.ts. */}
          {!loadingZones && accessZones.length > 0 && accessNotInEffect && (
            <div
              className={cn(
                'flex items-start gap-2 px-3 py-2 rounded-lg border text-xs',
                darkMode
                  ? 'bg-amber-900/20 border-amber-800/60 text-amber-300'
                  : 'bg-amber-50 border-amber-200 text-amber-800',
              )}
            >
              <AlertCircle className="w-3.5 h-3.5 mt-px flex-shrink-0" />
              <span>{accessNotInEffect}</span>
            </div>
          )}

          {loadingZones ? (
            <div className={cn(
              "p-4 rounded-lg border text-center",
              darkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            )}>
              <p className={cn(
                "text-sm",
                darkMode ? "text-gray-400" : "text-gray-500"
              )}>
                Carregando zonas de acesso...
              </p>
            </div>
          ) : accessZones.length > 0 ? (
            /* A reference list of where this person may go, PLUS the controls to fix one zone
               without re-syncing every zone. No state column: whether a credential actually
               reached a reader is the device table's answer, and repeating it here produced
               three stacked tables saying the same thing in different words.
               Revoking still goes through the confirmation in handleRevokeZoneAccess, so the
               destructive action is never one stray click. */
            <DeviceAccessTable
              darkMode={darkMode}
              columns={{ primary: 'Zona', middle: 'Perfil / Origem' }}
              emptyLabel="Nenhuma zona de acesso configurada"
              rows={accessZones.map((zone: any) => ({
                id: zone.id,
                primary: zone.name,
                secondary: zone.entry_type === 'entrada' ? 'Entrada'
                  : zone.entry_type === 'saida' ? 'Saída'
                  : zone.entry_type === 'passagem' ? 'Passagem'
                  : null,
                middle: (
                  <span className="block">
                    <span className="block truncate">{zone.profile_name}</span>
                    <span className={cn('block text-[10px]', darkMode ? 'text-gray-600' : 'text-gray-400')}>
                      {zone.source === 'direct' ? 'Direto' : person.customer?.type === 'empresa' ? 'Departamento' : 'Unidade'}
                    </span>
                  </span>
                ),
                actions: (
                  <>
                    <button
                      type="button"
                      onClick={() => handleSyncZone(zone.id, zone.name)}
                      disabled={syncingZoneId === zone.id || syncStatus.isSyncing}
                      title={`Sincronizar "${zone.name}"`}
                      aria-label={`Sincronizar ${zone.name}`}
                      className={cn(
                        'p-1.5 rounded-md transition-colors disabled:opacity-40',
                        darkMode ? 'text-blue-400 hover:bg-blue-500/10' : 'text-blue-600 hover:bg-blue-50',
                      )}
                    >
                      <RefreshCw className={cn('w-3.5 h-3.5', syncingZoneId === zone.id && 'animate-spin')} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRevokeZoneAccess(zone.id, zone.name)}
                      disabled={syncingZoneId === zone.id || syncStatus.isSyncing}
                      title={`Revogar "${zone.name}"`}
                      aria-label={`Revogar ${zone.name}`}
                      className={cn(
                        'p-1.5 rounded-md transition-colors disabled:opacity-40',
                        darkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50',
                      )}
                    >
                      <ShieldOff className="w-3.5 h-3.5" />
                    </button>
                  </>
                ),
              }))}
            />
          ) : (
            <div className={cn(
              "p-4 rounded-lg border text-center",
              darkMode ? "bg-gray-800 border-gray-700 text-gray-400" : "bg-gray-50 border-gray-200 text-gray-500"
            )}>
              <MapPin className={cn(
                "w-8 h-8 mx-auto mb-2",
                darkMode ? "text-gray-600" : "text-gray-300"
              )} />
              <p className="text-sm">Nenhuma zona de acesso configurada</p>
              <p className="text-xs mt-1">
                {person.customer?.type === 'empresa'
                  ? 'Configure perfis de acesso para o departamento desta pessoa'
                  : 'Configure perfis de acesso para a unidade desta pessoa'}
              </p>
            </div>
          )}

        </div>
      </div>

      {/* Dispositivos de Acesso follows Zonas de Acesso with nothing in between: the two
          answer one question together ("where may this person go, and did the credential
          actually get there"). The sync result and the note used to split them, pushing the
          device list a screen down from the zones it belongs to. */}
      <div className="mt-6">
        <RfidDevicesSection
          person={person}
          darkMode={darkMode}
          readOnly={readOnly}
          onRevokeDevice={confirmRevokeDevice}
        />
      </div>

      {/* The sync result used to be repeated here as a standing panel — the same counts and
          the same per-device reasons the sync card, its zoomed modal and the failure toast
          already report, left on screen long after the run. Dispositivos de Acesso above
          carries the lasting answer; this was the third copy of a transient one. */}

      {/* Device Audit Logs Section */}
      {person.customer && (
        <div className="mt-6">
          <h4 className={cn(
            "text-sm font-medium mb-3",
            darkMode ? "text-gray-300" : "text-gray-700"
          )}>
            Histórico de Sincronização e Revogação
          </h4>
          <PersonAuditLogTable
            customerId={person.customer.id}
            personId={person.id}
            darkMode={darkMode}
          />
        </div>
      )}

      {/* Device Details Modal */}
      {selectedDeviceForModal && (
        <DeviceDetailsModal
          device={selectedDeviceForModal}
          zone={selectedDeviceZone}
          onClose={() => {
            setSelectedDeviceForModal(null);
            setSelectedDeviceZone(null);
          }}
          onRefresh={() => {
            fetchAccessZones();
            onRefresh();
          }}
        />
      )}

      {/* Add Profile Modal — assign a profile directly to this person (same "exception"
          mechanism as Perfis de Acesso > Atribuir Perfil > Exceções, scoped from here) */}
      {showAddProfilePicker && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[65] p-4"
          onClick={() => { if (!assigningProfile) { setShowAddProfilePicker(false); setSelectedProfileToAdd(''); setProfilePreview(null); } }}
        >
          <div
            className={cn(
              "rounded-xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-y-auto border",
              darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={cn(
              "px-6 py-4 border-b flex items-center justify-between",
              darkMode ? "border-gray-700" : "border-gray-200"
            )}>
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-blue-500" />
                <h3 className={cn("text-base font-semibold", darkMode ? "text-white" : "text-gray-900")}>
                  Atribuir Perfil
                </h3>
              </div>
              <button
                onClick={() => { setShowAddProfilePicker(false); setSelectedProfileToAdd(''); setProfilePreview(null); }}
                disabled={assigningProfile}
                className={cn(
                  "p-1.5 rounded-lg transition-colors disabled:opacity-50",
                  darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
                )}
              >
                <X className="w-4.5 h-4.5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className={cn("block text-xs font-semibold uppercase tracking-wide mb-1.5", darkMode ? "text-gray-400" : "text-gray-500")}>
                  Selecione um Perfil
                </label>
                {availableProfiles.length === 0 ? (
                  <p className={cn("text-xs py-2", darkMode ? "text-gray-500" : "text-gray-400")}>
                    Nenhum perfil de acesso cadastrado para este cliente.
                  </p>
                ) : (
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5">
                    {availableProfiles.map(p => {
                      const isSelected = selectedProfileToAdd === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => handleSelectProfileToAdd(p.id)}
                          disabled={assigningProfile}
                          className={cn(
                            "w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors disabled:opacity-50",
                            isSelected
                              ? darkMode ? "border-blue-500 bg-blue-900/20" : "border-blue-500 bg-blue-50"
                              : darkMode ? "border-gray-700 hover:border-gray-600 hover:bg-gray-800/60" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          )}
                        >
                          <div className={cn(
                            "flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5",
                            isSelected
                              ? "border-blue-500 bg-blue-500"
                              : darkMode ? "border-gray-600" : "border-gray-300"
                          )}>
                            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn("text-sm font-medium", darkMode ? "text-gray-100" : "text-gray-900")}>
                              {p.name}
                            </p>
                            {p.description && (
                              <p className={cn("text-xs mt-0.5 truncate", darkMode ? "text-gray-500" : "text-gray-500")}>
                                {p.description}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Preview — description + what this profile actually grants. Devices are the
                  main point of interest here, so they get the visual weight; zones are just
                  a compact caption (their names already show up again per-device below,
                  repeating them as a second full list read as duplicated content). */}
              {selectedProfileToAdd && (
                <div className="space-y-3">
                  {(() => {
                    const selected = availableProfiles.find(p => p.id === selectedProfileToAdd);
                    return selected?.description ? (
                      <p className={cn("text-xs italic leading-relaxed", darkMode ? "text-gray-400" : "text-gray-500")}>
                        {selected.description}
                      </p>
                    ) : null;
                  })()}

                  {loadingProfilePreview ? (
                    <div className="flex items-center gap-2 py-2">
                      <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      <p className={cn("text-xs", darkMode ? "text-gray-500" : "text-gray-400")}>
                        Carregando dispositivos afetados...
                      </p>
                    </div>
                  ) : profilePreview && (
                    <>
                      {/* Zones — compact caption, not a competing list */}
                      <div className="flex items-center gap-1.5">
                        <MapPin className={cn("w-3.5 h-3.5 flex-shrink-0", darkMode ? "text-gray-500" : "text-gray-400")} />
                        <p className={cn("text-xs", darkMode ? "text-gray-500" : "text-gray-500")}>
                          {profilePreview.zones.length > 0
                            ? `${profilePreview.zones.length} zona(s) de acesso`
                            : 'Nenhuma zona configurada neste perfil'}
                        </p>
                      </div>

                      {/* Devices — the actual point: highlighted, spacious grid of chips */}
                      <div className={cn(
                        "rounded-lg border p-3",
                        darkMode ? "bg-blue-950/20 border-blue-900/50" : "bg-blue-50/60 border-blue-100"
                      )}>
                        <div className="flex items-center gap-2 mb-2.5">
                          <Monitor className={cn("w-4 h-4 flex-shrink-0", darkMode ? "text-blue-400" : "text-blue-600")} />
                          <p className={cn("text-sm font-semibold", darkMode ? "text-blue-300" : "text-blue-800")}>
                            {profilePreview.devices.length === 0
                              ? 'Nenhum dispositivo será liberado'
                              : `Acesso liberado em ${profilePreview.devices.length} dispositivo(s)`}
                          </p>
                        </div>
                        {profilePreview.devices.length > 0 && (
                          <div className={cn(
                            "rounded-lg border overflow-hidden max-h-56 overflow-y-auto",
                            darkMode ? "border-gray-700" : "border-gray-200"
                          )}>
                            {profilePreview.devices.map((d, i) => (
                              <div
                                key={d.id}
                                className={cn(
                                  "flex items-center gap-2.5 px-3 py-2 text-xs font-medium",
                                  i % 2 === 0
                                    ? darkMode ? "bg-gray-900/60 text-gray-200" : "bg-white text-gray-800"
                                    : darkMode ? "bg-gray-800/60 text-gray-200" : "bg-gray-50 text-gray-800"
                                )}
                              >
                                <Monitor className={cn("w-3.5 h-3.5 flex-shrink-0", darkMode ? "text-gray-500" : "text-gray-400")} />
                                <span className="truncate">{d.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={cn(
              "px-6 py-4 border-t flex justify-end gap-3",
              darkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-gray-50"
            )}>
              <button
                onClick={() => { setShowAddProfilePicker(false); setSelectedProfileToAdd(''); setProfilePreview(null); }}
                disabled={assigningProfile}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50",
                  darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"
                )}
              >
                Cancelar
              </button>
              <button
                onClick={handleAssignProfileDirect}
                disabled={!selectedProfileToAdd || assigningProfile}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
              >
                {assigningProfile ? 'Atribuindo...' : 'Atribuir Perfil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zone Details Modal */}
      {selectedZoneForModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          onClick={() => setSelectedZoneForModal(null)}
        >
          <div
            className={cn(
              "rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto",
              darkMode ? "bg-gray-800" : "bg-white"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={cn(
              "p-6 border-b sticky top-0",
              darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
            )}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <MapPin className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h2 className={cn(
                      "text-2xl font-bold truncate",
                      darkMode ? "text-gray-100" : "text-gray-900"
                    )}>
                      {selectedZoneForModal.name}
                    </h2>
                    <p className={cn(
                      "text-sm mt-1",
                      darkMode ? "text-gray-400" : "text-gray-600"
                    )}>
                      {translateZoneType(selectedZoneForModal.zone_type)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedZoneForModal(null)}
                  className={cn(
                    "p-2 rounded-lg transition-colors flex-shrink-0",
                    darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
                  )}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Zone Info */}
              <div>
                <h3 className={cn(
                  "text-sm font-semibold mb-3",
                  darkMode ? "text-gray-300" : "text-gray-700"
                )}>
                  Informações da Zona
                </h3>
                <div className={cn(
                  "grid grid-cols-2 gap-4 p-4 rounded-lg",
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                )}>
                  <div>
                    <p className={cn(
                      "text-xs",
                      darkMode ? "text-gray-400" : "text-gray-600"
                    )}>
                      Tipo de Zona
                    </p>
                    <p className={cn(
                      "font-medium mt-1",
                      darkMode ? "text-gray-100" : "text-gray-900"
                    )}>
                      {translateZoneType(selectedZoneForModal.zone_type)}
                    </p>
                  </div>
                  <div>
                    <p className={cn(
                      "text-xs",
                      darkMode ? "text-gray-400" : "text-gray-600"
                    )}>
                      Origem do Acesso
                    </p>
                    <p className={cn(
                      "font-medium mt-1",
                      darkMode ? "text-gray-100" : "text-gray-900"
                    )}>
                      {selectedZoneForModal.source === 'direct' && 'Direto'}
                      {selectedZoneForModal.source === 'person_type' && 'Tipo de pessoa'}
                      {selectedZoneForModal.source === 'department' && 'Departamento'}
                      {selectedZoneForModal.source === 'unit' && 'Unidade'}
                      {selectedZoneForModal.source === 'unknown' && 'Herança'}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className={cn(
                      "text-xs",
                      darkMode ? "text-gray-400" : "text-gray-600"
                    )}>
                      Perfil de Acesso
                    </p>
                    <p className={cn(
                      "font-medium mt-1",
                      darkMode ? "text-gray-100" : "text-gray-900"
                    )}>
                      {selectedZoneForModal.profile_name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Devices List */}
              <div>
                <h3 className={cn(
                  "text-sm font-semibold mb-3",
                  darkMode ? "text-gray-300" : "text-gray-700"
                )}>
                  Dispositivo
                </h3>
                {selectedZoneForModal.devices && selectedZoneForModal.devices.length > 0 ? (
                  <div className="space-y-2">
                    {selectedZoneForModal.devices.map((device) => (
                      <div
                        key={device.id}
                        className={cn(
                          "flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                          darkMode
                            ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                            : "bg-white border-gray-200 hover:bg-gray-50"
                        )}
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            // Fetch full device data
                            const deviceResponse = await api.get(`/iot-devices/${device.id}`);
                            const fullDevice = deviceResponse.data;

                            // Fetch zone data if device has zone_id
                            let zoneData = null;
                            if (fullDevice.zone_id) {
                              try {
                                const zoneResponse = await api.get(`/zones/${fullDevice.zone_id}`);
                                zoneData = zoneResponse.data;
                              } catch (err) {
                                console.error('Failed to fetch zone data:', err);
                              }
                            }

                            setSelectedDeviceForModal(fullDevice);
                            setSelectedDeviceZone(zoneData);
                          } catch (error) {
                            console.error('Failed to fetch device data:', error);
                            toast.error('Erro ao carregar dados do dispositivo');
                          }
                        }}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={cn(
                            "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                            device.is_synced
                              ? "bg-green-100 text-green-600"
                              : darkMode
                              ? "bg-gray-600 text-gray-400"
                              : "bg-gray-100 text-gray-400"
                          )}>
                            <Monitor className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              "font-medium truncate",
                              darkMode ? "text-gray-100" : "text-gray-900"
                            )}>
                              {device.name}
                            </p>
                            <p className={cn(
                              "text-xs mt-0.5",
                              darkMode ? "text-gray-400" : "text-gray-500"
                            )}>
                              {deviceTypeLabel(device.device_type)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {device.is_synced ? (
                            <span className="flex items-center gap-1.5 text-xs font-medium text-green-600">
                              <Check className="w-4 h-4" />
                              Sincronizado
                            </span>
                          ) : (
                            <span className={cn(
                              "text-xs font-medium",
                              darkMode ? "text-gray-400" : "text-gray-500"
                            )}>
                              Não sincronizado
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={cn(
                    "p-8 rounded-lg border-2 border-dashed text-center",
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gray-50 border-gray-300"
                  )}>
                    <Monitor className={cn(
                      "w-12 h-12 mx-auto mb-2",
                      darkMode ? "text-gray-500" : "text-gray-400"
                    )} />
                    <p className={cn(
                      "text-sm",
                      darkMode ? "text-gray-400" : "text-gray-500"
                    )}>
                      Nenhum dispositivo vinculado a esta zona
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className={cn(
              "p-6 border-t flex justify-end gap-3",
              darkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"
            )}>
              {!readOnly && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSyncZone(selectedZoneForModal.id, selectedZoneForModal.name);
                    }}
                    disabled={syncingZoneId === selectedZoneForModal.id || syncStatus.isSyncing}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
                      syncingZoneId === selectedZoneForModal.id || syncStatus.isSyncing
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    )}
                  >
                    <RefreshCw className={`w-4 h-4 ${syncingZoneId === selectedZoneForModal.id ? 'animate-spin' : ''}`} />
                    Sincronizar Zona
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRevokeZoneAccess(selectedZoneForModal.id, selectedZoneForModal.name);
                      setSelectedZoneForModal(null);
                    }}
                    disabled={syncStatus.isSyncing}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
                      syncStatus.isSyncing
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-red-600 text-white hover:bg-red-700"
                    )}
                  >
                    <ShieldOff className="w-4 h-4" />
                    Revogar Acesso
                  </button>
                </>
              )}
              <button
                onClick={() => setSelectedZoneForModal(null)}
                className={cn(
                  "px-4 py-2 rounded-lg font-medium transition-colors",
                  darkMode
                    ? "border border-gray-600 text-gray-300 hover:bg-gray-600"
                    : "border border-gray-300 text-gray-700 hover:bg-gray-100"
                )}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
